import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Checkbox } from '../ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../ui/sheet';
import {
  Target,
  DollarSign,
  Settings,
  Megaphone,
  Banknote,
  Shield,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  Save,
  Plus,
  Trash2,
  Edit,
  RefreshCw,
  Building2,
  ListTodo,
  MessageSquare,
  Users,
  Calendar,
  AtSign,
  Send,
  Circle,
  CheckCircle2,
  ClipboardList,
  ShieldCheck,
  FileText,
  X,
  Pencil,
  Bell,
  GripVertical,
  Minimize2,
  Maximize2,
  CheckCheck,
  ArrowRightLeft,
  MessageCircle,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Import extracted components from FivePillars folder
import { TimelineDialog } from './FivePillars/TimelineDialog';
import { ImplementationDialog } from './FivePillars/ImplementationDialog';
import { AddFeatureDialog } from './FivePillars/AddFeatureDialog';
import { FeaturePillarCard, FeatureDetailSheet } from './FivePillars/FeatureComponents';
// Import EnhancedScopeModal for consistent scope management with main 5 Pillars Hub
import { EnhancedScopeModal } from '../five-pillars/dialogs';

const API = process.env.REACT_APP_BACKEND_URL;

// Leadership roles that can send suggestions
const LEADERSHIP_ROLES = ['ceo', 'president', 'president_&_ceo', 'director_of_sales', 'general_manager', 'leadership', 'director', 'vp'];

// Icon mapping
const ICON_MAP = {
  'target': Target,
  'dollar-sign': DollarSign,
  'settings': Settings,
  'megaphone': Megaphone,
  'banknote': Banknote,
  'shield': Shield,
  'building': Building2,
  'calendar': Calendar,
  'clipboard-list': ClipboardList,
  'shield-check': ShieldCheck,
  'file-text': FileText,
};

// Color mapping
const COLOR_MAP = {
  green: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-600 dark:text-green-400' },
  blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-600 dark:text-blue-400' },
  purple: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-600 dark:text-purple-400' },
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-600 dark:text-emerald-400' },
  slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-600 dark:text-slate-400' },
  amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-600 dark:text-amber-400' },
  red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-600 dark:text-red-400' },
  indigo: { bg: 'bg-indigo-500/10', border: 'border-indigo-500/30', text: 'text-indigo-600 dark:text-indigo-400' },
};

// Status styling
const STATUS_STYLES = {
  not_started: { label: 'Not Started', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
  planning: { label: 'Planning', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  in_progress: { label: 'In Progress', color: 'bg-amber-500/10 text-amber-600 border-amber-500/20' },
  testing: { label: 'Testing', color: 'bg-blue-500/10 text-blue-600 border-blue-500/20' },
  live: { label: 'Live', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  on_hold: { label: 'On Hold', color: 'bg-red-500/10 text-red-600 border-red-500/20' },
  completed: { label: 'Completed', color: 'bg-green-500/10 text-green-600 border-green-500/20' },
  pending: { label: 'Pending', color: 'bg-slate-500/10 text-slate-600 border-slate-500/20' },
};

const HEALTH_STYLES = {
  green: { icon: CheckCircle, color: 'text-green-500', label: 'On Track' },
  yellow: { icon: AlertTriangle, color: 'text-amber-500', label: 'At Risk' },
  red: { icon: XCircle, color: 'text-red-500', label: 'Blocked' },
};

// ==================== GOVERNANCE PILLAR CARD ====================
const GovernancePillarCard = ({ pillar, onClick, isAdmin, isLeadership, currentUserId, scopePillar }) => {
  const IconComponent = ICON_MAP[pillar.icon] || Target;
  const colors = COLOR_MAP[pillar.color] || COLOR_MAP.indigo;
  
  // Count unresolved suggestions for admin notification (exclude admin's own suggestions)
  const unresolvedSuggestions = pillar.suggestions?.filter(s => !s.resolved && !s.is_admin && s.user_id !== currentUserId).length || 0;
  
  // Count suggestions with admin replies that leadership hasn't SEEN yet
  const unseenAdminReplies = pillar.suggestions?.filter(s => {
    if (!s.replies || s.replies.length === 0) return false;
    // Don't notify the user about their own replies
    const lastReply = s.replies[s.replies.length - 1];
    if (lastReply.user_id === currentUserId) return false;
    const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
    const hasSeen = s.seen_by && s.seen_by.includes(currentUserId);
    return hasAdminReply && !hasSeen;
  }).length || 0;

  // Count tasks created/updated by leadership that admin hasn't seen (for admin notification)
  const countUnseenTaskUpdatesForAdmin = () => {
    let count = 0;
    
    // Handle legacy array structure
    const legacyItems = pillar.content?.key_deliverables || pillar.content?.key_milestones || [];
    if (Array.isArray(legacyItems)) {
      legacyItems.forEach(item => {
        (item.tasks || []).forEach(task => {
          const createdByLeadership = task.assigned_by && !['super_admin', 'admin'].includes(task.created_by_role);
          const hasUnseenUpdates = task.updates?.some(u => !u.seen_by?.includes(currentUserId) && !u.is_admin);
          const taskNotSeen = !task.seen_by?.includes(currentUserId) && createdByLeadership;
          if (taskNotSeen || hasUnseenUpdates) count++;
        });
      });
    }
    
    // Handle new phases object structure (for Scope)
    const phasesObj = pillar.content?.phases;
    if (phasesObj && typeof phasesObj === 'object') {
      Object.values(phasesObj).forEach(phaseItems => {
        if (Array.isArray(phaseItems)) {
          phaseItems.forEach(item => {
            (item.tasks || []).forEach(task => {
              const createdByLeadership = task.assigned_by && !['super_admin', 'admin'].includes(task.created_by_role);
              const hasUnseenUpdates = task.updates?.some(u => !u.seen_by?.includes(currentUserId) && !u.is_admin);
              const taskNotSeen = !task.seen_by?.includes(currentUserId) && createdByLeadership;
              if (taskNotSeen || hasUnseenUpdates) count++;
            });
          });
        }
      });
    }
    
    return count;
  };
  
  // Count admin updates on tasks that leadership hasn't seen (for leadership notification)
  const countUnseenAdminTaskUpdates = () => {
    let count = 0;
    
    // Handle legacy array structure (key_deliverables, key_milestones)
    const legacyItems = pillar.content?.key_deliverables || pillar.content?.key_milestones || [];
    if (Array.isArray(legacyItems)) {
      legacyItems.forEach(item => {
        (item.tasks || []).forEach(task => {
          const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(currentUserId));
          const completedByAdmin = task.completed && task.completed_by && !task.completion_seen_by?.includes(currentUserId);
          if (hasUnseenAdminUpdates || completedByAdmin) count++;
        });
      });
    }
    
    // Handle new phases object structure (for Scope)
    const phasesObj = pillar.content?.phases;
    if (phasesObj && typeof phasesObj === 'object') {
      Object.values(phasesObj).forEach(phaseItems => {
        if (Array.isArray(phaseItems)) {
          phaseItems.forEach(item => {
            (item.tasks || []).forEach(task => {
              const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(currentUserId));
              const completedByAdmin = task.completed && task.completed_by && !task.completion_seen_by?.includes(currentUserId);
              if (hasUnseenAdminUpdates || completedByAdmin) count++;
            });
          });
        }
      });
    }
    
    return count;
  };
  
  const unseenTaskUpdatesForAdmin = isAdmin ? countUnseenTaskUpdatesForAdmin() : 0;
  const unseenAdminTaskUpdates = isLeadership && !isAdmin ? countUnseenAdminTaskUpdates() : 0;

  // Calculate overall progress from deliverables/items with tasks
  const calculateProgress = () => {
    let totalTasks = 0;
    let completedTasks = 0;
    
    // Handle phases object structure (Scope pillar - new format)
    const phasesObj = pillar.content?.phases;
    const subPhasesObj = pillar.content?.sub_phases;
    
    if (phasesObj && typeof phasesObj === 'object') {
      Object.entries(phasesObj).forEach(([phaseId, phaseItems]) => {
        // Skip future_features and non-array values
        if (phaseId === 'future_features' || !Array.isArray(phaseItems)) return;
        phaseItems.forEach(item => {
          if (item.tasks && item.tasks.length > 0) {
            totalTasks += item.tasks.length;
            completedTasks += item.tasks.filter(t => t.completed).length;
          }
        });
      });
      
      // Also count sub-phase tasks
      if (subPhasesObj && typeof subPhasesObj === 'object') {
        Object.values(subPhasesObj).forEach(subPhases => {
          if (Array.isArray(subPhases)) {
            subPhases.forEach(subPhase => {
              const deliverables = subPhase.deliverables || [];
              deliverables.forEach(item => {
                if (item.tasks && item.tasks.length > 0) {
                  totalTasks += item.tasks.length;
                  completedTasks += item.tasks.filter(t => t.completed).length;
                }
              });
            });
          }
        });
      }
    } else {
      // Handle legacy array structures (key_deliverables, key_milestones) ONLY if no phases
      const legacyItems = pillar.content?.key_deliverables || pillar.content?.key_milestones || [];
      if (Array.isArray(legacyItems)) {
        legacyItems.forEach(item => {
          if (item.tasks && item.tasks.length > 0) {
            totalTasks += item.tasks.length;
            completedTasks += item.tasks.filter(t => t.completed).length;
          }
        });
      }
    }
    
    if (totalTasks === 0) return 0; // Return 0% when no tasks
    return Math.round((completedTasks / totalTasks) * 100);
  };
  
  const progress = calculateProgress();
  
  // Status color mapping for phase status badges on card
  const PHASE_STATUS_COLORS = {
    planning: { bg: 'bg-slate-500', text: 'text-slate-500', label: 'Planning' },
    design: { bg: 'bg-purple-500', text: 'text-purple-500', label: 'Design' },
    building: { bg: 'bg-blue-400', text: 'text-blue-400', label: 'Building' },
    coding: { bg: 'bg-blue-600', text: 'text-blue-600', label: 'Coding' },
    internal_testing: { bg: 'bg-cyan-500', text: 'text-cyan-500', label: 'Internal Testing' },
    team_testing: { bg: 'bg-teal-500', text: 'text-teal-500', label: 'Team Testing' },
    debug: { bg: 'bg-orange-500', text: 'text-orange-500', label: 'De-Bug' },
    edit_upgrades: { bg: 'bg-amber-500', text: 'text-amber-500', label: 'Edit/Upgrades' },
    final_testing: { bg: 'bg-indigo-500', text: 'text-indigo-500', label: 'Final Testing' },
    release_ready: { bg: 'bg-emerald-500', text: 'text-emerald-500', label: 'Release Ready' },
    needs_attention: { bg: 'bg-red-500', text: 'text-red-500', label: 'Needs Attention' },
    completed: { bg: 'bg-green-500', text: 'text-green-500', label: 'Completed' },
  };

  // Calculate Scope-specific stats (deliverables, tasks, and phase statuses)
  const getScopeStats = () => {
    if (pillar.pillar_type !== 'scope') return null;
    
    const phasesObj = pillar.content?.phases || {};
    const subPhasesObj = pillar.content?.sub_phases || {};
    const phaseStatuses = pillar.content?.phase_statuses || {};
    const phaseNames = pillar.content?.phase_names || {};
    const customPhases = pillar.content?.custom_phases || [];
    const defaultPhaseIds = ['phase1', 'phase2', 'phase3', 'phase4', 'phase5', 'phase6', 'phase7'];
    const allPhaseIds = [...defaultPhaseIds, ...customPhases];
    
    let totalDeliverables = 0;
    let totalTasks = 0;
    let completedTasks = 0;
    let activeTasks = 0;
    let totalSubPhases = 0;
    
    // Get phases with deliverables and their statuses
    const phasesWithStatus = [];
    
    // Count deliverables and tasks across all phases (excluding future_features and exclusions)
    allPhaseIds.forEach((phaseId, idx) => {
      const phaseItems = phasesObj[phaseId] || [];
      const phaseSubPhases = subPhasesObj[phaseId] || [];
      
      let phaseDeliverableCount = 0;
      let phaseTasksCompleted = 0;
      let phaseTasksTotal = 0;
      
      // Count phase-level deliverables and tasks
      if (Array.isArray(phaseItems)) {
        phaseDeliverableCount += phaseItems.length;
        phaseItems.forEach(item => {
          const tasks = item.tasks || [];
          phaseTasksTotal += tasks.length;
          phaseTasksCompleted += tasks.filter(t => t.completed).length;
        });
      }
      
      // Count sub-phase deliverables and tasks
      if (Array.isArray(phaseSubPhases)) {
        totalSubPhases += phaseSubPhases.length;
        phaseSubPhases.forEach(subPhase => {
          const subPhaseDeliverables = subPhase.deliverables || [];
          phaseDeliverableCount += subPhaseDeliverables.length;
          subPhaseDeliverables.forEach(item => {
            const tasks = item.tasks || [];
            phaseTasksTotal += tasks.length;
            phaseTasksCompleted += tasks.filter(t => t.completed).length;
          });
        });
      }
      
      totalDeliverables += phaseDeliverableCount;
      totalTasks += phaseTasksTotal;
      completedTasks += phaseTasksCompleted;
      activeTasks += phaseTasksTotal - phaseTasksCompleted;
      
      // Get phase name and status
      const phaseName = phaseNames[phaseId] || `Phase ${idx + 1}`;
      const status = phaseStatuses[phaseId] || 'planning';
      
      // Only include phases that have deliverables OR have a non-default status
      if (phaseDeliverableCount > 0 || status !== 'planning') {
        phasesWithStatus.push({
          id: phaseId,
          name: phaseName,
          status,
          deliverables: phaseDeliverableCount,
          tasksCompleted: phaseTasksCompleted,
          tasksTotal: phaseTasksTotal
        });
      }
    });
    
    return { totalDeliverables, totalTasks, completedTasks, activeTasks, totalSubPhases, phasesWithStatus };
  };
  
  // Get phase statuses for non-scope pillars (uses scopePillar as source of truth)
  const getPhaseStatusesForPillar = () => {
    // For Scope pillar, use its own data (handled by getScopeStats)
    if (pillar.pillar_type === 'scope') return null;
    
    // For other pillars, use scopePillar data if available
    if (!scopePillar) return null;
    
    const phaseStatuses = scopePillar.content?.phase_statuses || {};
    const phaseNames = scopePillar.content?.phase_names || {};
    const customPhases = scopePillar.content?.custom_phases || [];
    const phasesObj = scopePillar.content?.phases || {};
    const defaultPhaseIds = ['phase1', 'phase2', 'phase3', 'phase4', 'phase5', 'phase6', 'phase7'];
    const allPhaseIds = [...defaultPhaseIds, ...customPhases];
    
    const phasesWithStatus = [];
    
    allPhaseIds.forEach((phaseId, idx) => {
      const phaseItems = phasesObj[phaseId] || [];
      const deliverableCount = Array.isArray(phaseItems) ? phaseItems.length : 0;
      const phaseName = phaseNames[phaseId] || `Phase ${idx + 1}`;
      const status = phaseStatuses[phaseId] || 'planning';
      
      // Only include phases that have deliverables OR have a non-default status
      if (deliverableCount > 0 || status !== 'planning') {
        phasesWithStatus.push({
          id: phaseId,
          name: phaseName,
          status
        });
      }
    });
    
    return phasesWithStatus.length > 0 ? phasesWithStatus : null;
  };
  
  const scopeStats = getScopeStats();
  const pillarPhaseStatuses = getPhaseStatusesForPillar();
  
  // Total notifications for admin (suggestions + task updates from leadership)
  const adminNotifications = unresolvedSuggestions + unseenTaskUpdatesForAdmin;
  
  // Total notifications for leadership (admin replies on suggestions + admin task updates)
  const leadershipNotifications = unseenAdminReplies + unseenAdminTaskUpdates;

  return (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${colors.border} border-2 relative h-full min-h-[120px] overflow-hidden`}
      onClick={() => onClick(pillar)}
    >
      {/* Admin notification badge for unresolved suggestions + task updates */}
      {isAdmin && adminNotifications > 0 && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-md animate-pulse">
            {adminNotifications}
          </span>
        </div>
      )}
      {/* Leadership notification badge for UNSEEN admin replies + task updates */}
      {isLeadership && !isAdmin && leadershipNotifications > 0 && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-blue-500 rounded-full shadow-md animate-pulse">
            {leadershipNotifications}
          </span>
        </div>
      )}
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          <div className={`p-2 rounded-lg ${colors.bg} flex-shrink-0`}>
            <IconComponent className={`h-4 w-4 ${colors.text}`} />
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <h4 className="font-semibold text-xs truncate">{pillar.name}</h4>
            {/* Scope-specific stats */}
            {scopeStats && (
              <div className="flex items-center gap-1 mt-1 flex-wrap">
                <Badge variant="outline" className="text-[8px] px-1 py-0 h-4">
                  {scopeStats.totalDeliverables} items
                </Badge>
                {scopeStats.totalSubPhases > 0 && (
                  <Badge variant="outline" className="text-[8px] px-1 py-0 h-4 border-cyan-500/50 text-cyan-500">
                    {scopeStats.totalSubPhases} sub
                  </Badge>
                )}
                <Badge variant="outline" className={`text-[8px] px-1 py-0 h-4 ${scopeStats.completedTasks > 0 ? 'border-green-500/50 text-green-600' : ''}`}>
                  {scopeStats.completedTasks}/{scopeStats.totalTasks}
                </Badge>
              </div>
            )}
            {/* Phase statuses for Scope card - limit to 3 */}
            {scopeStats && scopeStats.phasesWithStatus && scopeStats.phasesWithStatus.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {scopeStats.phasesWithStatus.slice(0, 3).map((phase) => {
                  const statusConfig = PHASE_STATUS_COLORS[phase.status] || PHASE_STATUS_COLORS.planning;
                  return (
                    <div key={phase.id} className="flex items-center gap-1 text-[8px]">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig.bg}`} />
                      <span className="text-muted-foreground truncate max-w-[50px]">{phase.name}</span>
                      <span className={`font-medium truncate ${statusConfig.text}`}>{statusConfig.label}</span>
                    </div>
                  );
                })}
                {scopeStats.phasesWithStatus.length > 3 && (
                  <span className="text-[8px] text-muted-foreground">+{scopeStats.phasesWithStatus.length - 3} more</span>
                )}
              </div>
            )}
            {/* Phase statuses for non-Scope pillar cards - limit to 3 */}
            {pillarPhaseStatuses && pillarPhaseStatuses.length > 0 && (
              <div className="mt-1.5 space-y-0.5">
                {pillarPhaseStatuses.slice(0, 3).map((phase) => {
                  const statusConfig = PHASE_STATUS_COLORS[phase.status] || PHASE_STATUS_COLORS.planning;
                  return (
                    <div key={phase.id} className="flex items-center gap-1 text-[8px]">
                      <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusConfig.bg}`} />
                      <span className="text-muted-foreground truncate max-w-[50px]">{phase.name}</span>
                      <span className={`font-medium truncate ${statusConfig.text}`}>{statusConfig.label}</span>
                    </div>
                  );
                })}
                {pillarPhaseStatuses.length > 3 && (
                  <span className="text-[8px] text-muted-foreground">+{pillarPhaseStatuses.length - 3} more</span>
                )}
              </div>
            )}
            {progress !== null ? (
              <div className="flex items-center gap-1 mt-1">
                <Progress value={progress} className="h-1 flex-1" />
                <span className="text-[8px] font-medium text-muted-foreground">{progress}%</span>
              </div>
            ) : !scopeStats && pillar.last_updated && (
              <p className="text-[8px] text-muted-foreground truncate">
                {new Date(pillar.last_updated).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== NOTES/FEEDBACK SECTION WITH @ MENTIONS AND SIDEBAR ====================
const NotesSection = ({ notes = [], isAdmin, isLeadership, currentUser, onAddNote, onAddReply, allUsers = [], title = "Notes" }) => {
  const [newNote, setNewNote] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(null); // noteId for reply sidebar
  const [replyContent, setReplyContent] = useState('');
  const [replyMentions, setReplyMentions] = useState([]);
  const [showReplyMentions, setShowReplyMentions] = useState(false);
  const scrollRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const handleNoteChange = (e) => {
    const value = e.target.value;
    setNewNote(value);
    
    // Check for @ mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1 && lastAtIndex === value.length - 1) {
      setShowMentions(true);
      setMentionSearch('');
    } else if (lastAtIndex !== -1) {
      const textAfterAt = value.substring(lastAtIndex + 1);
      if (!textAfterAt.includes(' ')) {
        setShowMentions(true);
        setMentionSearch(textAfterAt.toLowerCase());
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user, isReply = false) => {
    if (isReply) {
      const lastAtIndex = replyContent.lastIndexOf('@');
      const beforeAt = replyContent.substring(0, lastAtIndex);
      setReplyContent(`${beforeAt}@${user.name} `);
      setReplyMentions([...replyMentions, user.id]);
      setShowReplyMentions(false);
    } else {
      const lastAtIndex = newNote.lastIndexOf('@');
      const beforeAt = newNote.substring(0, lastAtIndex);
      setNewNote(`${beforeAt}@${user.name} `);
      setSelectedMentions([...selectedMentions, user.id]);
      setShowMentions(false);
    }
  };

  const handleSubmit = () => {
    if (!newNote.trim()) return;
    onAddNote({ content: newNote.trim(), mentions: selectedMentions });
    setNewNote('');
    setSelectedMentions([]);
  };

  const handleReplySubmit = (noteId) => {
    if (!replyContent.trim()) return;
    onAddReply(noteId, { content: replyContent.trim(), mentions: replyMentions });
    setReplyContent('');
    setReplyMentions([]);
    setSidebarOpen(null);
  };

  const filteredUsers = allUsers.filter(u => 
    u.name.toLowerCase().includes(mentionSearch)
  );

  const formatContent = (content) => {
    // Highlight @mentions
    return content.replace(/@(\w+\s?\w*)/g, '<span class="text-blue-400 font-medium">@$1</span>');
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold">{title}</Label>
        <Badge variant="outline" className="text-[10px]">{notes.length} entries</Badge>
      </div>

      {/* Notes Feed */}
      <ScrollArea className="h-[200px] rounded border bg-slate-800/50 p-2" ref={scrollRef}>
        {notes.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">No notes yet. Add one below.</p>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <div key={note.id} className="p-2 rounded bg-slate-700/50 border border-slate-600">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-blue-400">{note.author}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                    <p className="text-sm" dangerouslySetInnerHTML={{ __html: formatContent(note.content) }} />
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 text-[10px] px-2"
                    onClick={() => setSidebarOpen(note.id)}
                  >
                    <MessageCircle className="h-3 w-3 mr-1" />
                    {note.replies?.length || 0}
                  </Button>
                </div>
                {note.replies?.length > 0 && (
                  <div className="mt-2 pl-3 border-l-2 border-slate-600 space-y-1">
                    {note.replies.slice(-2).map((reply) => (
                      <div key={reply.id} className="text-xs">
                        <span className="font-medium text-emerald-400">{reply.author}</span>
                        <span className="text-muted-foreground"> · </span>
                        <span dangerouslySetInnerHTML={{ __html: formatContent(reply.content) }} />
                      </div>
                    ))}
                    {note.replies.length > 2 && (
                      <button 
                        className="text-[10px] text-blue-400 hover:underline"
                        onClick={() => setSidebarOpen(note.id)}
                      >
                        View all {note.replies.length} replies
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Add Note Input */}
      {(isAdmin || isLeadership) && (
        <div className="relative">
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Textarea
                ref={inputRef}
                value={newNote}
                onChange={handleNoteChange}
                placeholder="Add a note... Use @ to mention users"
                rows={2}
                className="text-xs pr-8"
              />
              {showMentions && filteredUsers.length > 0 && (
                <div className="absolute bottom-full left-0 w-full bg-slate-800 border border-slate-600 rounded shadow-lg z-50 max-h-32 overflow-y-auto">
                  {filteredUsers.slice(0, 5).map(user => (
                    <button
                      key={user.id}
                      className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-700 flex items-center gap-2"
                      onClick={() => insertMention(user)}
                    >
                      <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center text-[10px] text-white">
                        {user.avatar || user.name[0]}
                      </div>
                      {user.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button size="sm" onClick={handleSubmit} disabled={!newNote.trim()}>
              <Send className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Reply Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-y-0 right-0 w-80 bg-slate-900 border-l border-slate-700 shadow-xl z-50 flex flex-col">
          <div className="p-3 border-b border-slate-700 flex items-center justify-between">
            <Label className="font-semibold">Replies</Label>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setSidebarOpen(null)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <ScrollArea className="flex-1 p-3">
            {(() => {
              const note = notes.find(n => n.id === sidebarOpen);
              if (!note) return null;
              return (
                <div className="space-y-3">
                  {/* Original note */}
                  <div className="p-2 rounded bg-slate-800 border border-slate-600">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-medium text-blue-400">{note.author}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(note.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm" dangerouslySetInnerHTML={{ __html: formatContent(note.content) }} />
                  </div>
                  {/* Replies */}
                  {note.replies?.map((reply) => (
                    <div key={reply.id} className="p-2 rounded bg-slate-700/50 ml-4 border-l-2 border-emerald-500/50">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-emerald-400">{reply.author}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {new Date(reply.created_at).toLocaleDateString()} {new Date(reply.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                        </span>
                      </div>
                      <p className="text-xs" dangerouslySetInnerHTML={{ __html: formatContent(reply.content) }} />
                    </div>
                  ))}
                </div>
              );
            })()}
          </ScrollArea>
          {/* Reply Input */}
          {(isAdmin || isLeadership) && (
            <div className="p-3 border-t border-slate-700">
              <div className="relative">
                <Textarea
                  value={replyContent}
                  onChange={(e) => {
                    setReplyContent(e.target.value);
                    const val = e.target.value;
                    const lastAt = val.lastIndexOf('@');
                    if (lastAt !== -1 && !val.substring(lastAt + 1).includes(' ')) {
                      setShowReplyMentions(true);
                    } else {
                      setShowReplyMentions(false);
                    }
                  }}
                  placeholder="Reply... Use @ to mention"
                  rows={2}
                  className="text-xs"
                />
                {showReplyMentions && (
                  <div className="absolute bottom-full left-0 w-full bg-slate-800 border border-slate-600 rounded shadow-lg z-50 max-h-32 overflow-y-auto">
                    {allUsers.slice(0, 5).map(user => (
                      <button
                        key={user.id}
                        className="w-full px-3 py-1.5 text-left text-xs hover:bg-slate-700"
                        onClick={() => insertMention(user, true)}
                      >
                        {user.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button size="sm" className="w-full mt-2" onClick={() => handleReplySubmit(sidebarOpen)} disabled={!replyContent.trim()}>
                <Send className="h-3 w-3 mr-1" /> Send Reply
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== FEEDBACK SECTION FOR GOVERNANCE PILLARS ====================
const GovernanceFeedbackSection = ({ pillar, isAdmin, isLeadership, currentUser, onSave }) => {
  const [newSuggestion, setNewSuggestion] = useState('');
  const [suggestions, setSuggestions] = useState(pillar?.suggestions || []);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(null);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const scrollRef = React.useRef(null);
  const hasMarkedSeen = React.useRef(false);

  // Team members for @ mentions
  const allUsers = [
    { id: 'ryan_march', name: 'Ryan March', avatar: 'RM' },
    { id: 'ryan_adams', name: 'Ryan Adams', avatar: 'RA' },
    { id: 'gabe', name: 'Gabriel Gecan', avatar: 'GG' },
  ];

  useEffect(() => {
    setSuggestions(pillar?.suggestions || []);
    hasMarkedSeen.current = false; // Reset when pillar changes
  }, [pillar]);

  // Auto-scroll to bottom when suggestions or replies change
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [suggestions]);

  const handleSuggestionChange = (e) => {
    const value = e.target.value;
    setNewSuggestion(value);
    const lastAt = value.lastIndexOf('@');
    if (lastAt !== -1 && !value.substring(lastAt + 1).includes(' ')) {
      setShowMentions(true);
      setMentionSearch(value.substring(lastAt + 1).toLowerCase());
    } else {
      setShowMentions(false);
    }
  };

  const insertMention = (user) => {
    const lastAt = newSuggestion.lastIndexOf('@');
    const before = newSuggestion.substring(0, lastAt);
    setNewSuggestion(`${before}@${user.name} `);
    setShowMentions(false);
  };

  const formatContent = (content) => {
    return content?.replace(/@(\w+\s?\w*)/g, '<span class="text-blue-400 font-medium">@$1</span>') || '';
  };

  // Mark suggestions as seen when leadership views them (only once per dialog open)
  useEffect(() => {
    if (isLeadership && !isAdmin && !hasMarkedSeen.current && suggestions.length > 0) {
      const userId = currentUser?.id;
      const needsUpdate = suggestions.some(s => {
        const lastReply = s.replies?.[s.replies.length - 1];
        const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
        return hasAdminReply && (!s.seen_by || !s.seen_by.includes(userId));
      });
      
      if (needsUpdate) {
        const updatedSuggestions = suggestions.map(s => {
          const lastReply = s.replies?.[s.replies.length - 1];
          const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
          if (hasAdminReply) {
            const seenBy = s.seen_by || [];
            if (!seenBy.includes(userId)) {
              return { ...s, seen_by: [...seenBy, userId] };
            }
          }
          return s;
        });
        setSuggestions(updatedSuggestions);
        hasMarkedSeen.current = true;
        // Save to backend
        onSave(pillar.id, { suggestions: updatedSuggestions }).catch(() => {});
      }
    }
  }, [isLeadership, isAdmin, suggestions, currentUser, onSave, pillar?.id]);

  const handleAddSuggestion = async () => {
    if (!newSuggestion.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || (isAdmin ? 'Admin' : 'Leadership');
    const suggestion = {
      id: `sug_${Date.now()}`,
      content: newSuggestion.trim(),
      user_id: currentUser?.id,
      user_name: userName,
      user_role: currentUser?.role,
      is_admin: isAdmin,
      created_at: new Date().toISOString(),
      status: 'sent', // Default status
      resolved: false,
      replies: []
    };
    const updatedSuggestions = [...suggestions, suggestion];
    setSuggestions(updatedSuggestions);
    setNewSuggestion('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success('Note posted');
    } catch (e) {
      toast.error('Failed to post');
    }
  };

  const handleResolveSuggestion = async (suggestionId, resolveNote = '') => {
    const adminName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const updatedSuggestions = suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        resolved: true, 
        resolved_at: new Date().toISOString(),
        resolved_by: adminName,
        resolve_note: resolveNote || null
      } : s
    );
    setSuggestions(updatedSuggestions);
    setReplyingTo(null);
    setReplyText('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success('Suggestion resolved');
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const handleAddReply = async (suggestionId) => {
    if (!replyText.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'User';
    const reply = {
      id: `reply_${Date.now()}`,
      content: replyText.trim(),
      user_id: currentUser?.id,
      user_name: userName,
      user_role: currentUser?.role,
      is_admin: isAdmin,
      created_at: new Date().toISOString()
    };
    // Don't auto-resolve when admin replies - let them manually control status
    const updatedSuggestions = suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        replies: [...(s.replies || []), reply],
        // Update status to 'received' if it was 'sent' and admin replies
        ...(isAdmin && s.status === 'sent' ? { status: 'received' } : {})
      } : s
    );
    setSuggestions(updatedSuggestions);
    setReplyingTo(null);
    setReplyText('');
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success('Reply sent');
    } catch (e) {
      toast.error('Failed to send reply');
    }
  };

  // Update suggestion status (admin only)
  const handleUpdateSuggestionStatus = async (suggestionId, newStatus) => {
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const updatedSuggestions = suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        status: newStatus,
        resolved: newStatus === 'resolved',
        ...(newStatus === 'resolved' ? {
          resolved_at: new Date().toISOString(),
          resolved_by: userName
        } : {
          resolved_at: null,
          resolved_by: null
        })
      } : s
    );
    setSuggestions(updatedSuggestions);
    try {
      await onSave(pillar.id, { suggestions: updatedSuggestions });
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  // Note status options
  const NOTE_STATUS_OPTIONS = [
    { value: 'sent', label: 'Sent', color: 'bg-slate-500' },
    { value: 'received', label: 'Received', color: 'bg-blue-500' },
    { value: 'planning', label: 'Planning', color: 'bg-purple-500' },
    { value: 'building', label: 'Building', color: 'bg-amber-500' },
    { value: 'coding', label: 'Coding', color: 'bg-cyan-500' },
    { value: 'ready', label: 'Ready', color: 'bg-emerald-500' },
    { value: 'resolved', label: 'Resolved', color: 'bg-green-500' }
  ];

  if (!isLeadership && !isAdmin) return null;

  const unresolvedCount = suggestions.filter(s => s.status !== 'resolved').length;

  // Helper to determine reply style based on user role - show actual user info
  const getReplyStyle = (reply) => {
    if (reply.is_admin || reply.user_role === 'super_admin' || reply.user_role === 'admin') {
      return { bg: 'bg-blue-500/10', labelColor: 'text-blue-700', label: reply.user_role?.replace('_', ' ') || 'Admin' };
    }
    return { bg: 'bg-amber-500/10', labelColor: 'text-amber-700', label: 'Leadership' };
  };

  return (
    <div className="border-t pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-medium flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          Feedback & Suggestions
          {isAdmin && unresolvedCount > 0 && (
            <Badge variant="destructive" className="text-[10px]">{unresolvedCount} new</Badge>
          )}
        </Label>
        {suggestions.length > 2 && (
          <span className="text-[10px] text-muted-foreground">↕ Scroll for more</span>
        )}
      </div>
      
      {/* Show suggestions - chronological order (oldest first, newest at bottom) */}
      <ScrollArea className="h-[300px] border rounded-lg p-2 bg-background/50" ref={scrollRef}>
        <div className="space-y-3">
          {suggestions.length > 0 ? suggestions.map((sug) => {
            // Check if last reply is from admin (leadership needs to see/respond)
            const lastReply = sug.replies?.[sug.replies.length - 1];
            const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
            // Leadership can reply if: suggestion has admin reply they haven't responded to
            const leadershipCanReply = isLeadership && !isAdmin && hasAdminReply;
            // Check if this is unseen by current user (for highlighting)
            const isUnseen = isLeadership && !isAdmin && hasAdminReply && (!sug.seen_by || !sug.seen_by.includes(currentUser?.id));
            
            // Get current status (default to 'sent' for backwards compatibility)
            const currentStatus = sug.status || 'sent';
            const statusConfig = NOTE_STATUS_OPTIONS.find(s => s.value === currentStatus) || NOTE_STATUS_OPTIONS[0];
            
            // Determine card styling based on state
            let cardStyle = currentStatus === 'resolved' ? 'bg-muted/30 border border-muted' : 'bg-amber-500/10 border border-amber-500/30';
            if (isUnseen) {
              cardStyle = 'bg-blue-500/10 border-2 border-blue-500 ring-2 ring-blue-500/20';
            }
            
            return (
            <div key={sug.id} className={`p-3 rounded-lg text-sm ${cardStyle} relative`}>
              {/* NEW badge for unseen messages */}
              {isUnseen && (
                <div className="absolute -top-2 -right-2">
                  <Badge className="bg-blue-500 text-white text-[9px] px-1.5 animate-pulse">NEW</Badge>
                </div>
              )}
              {/* Suggestion Header */}
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-xs">{sug.user_name} <span className="text-muted-foreground">({sug.user_role?.replace('_', ' ')})</span></span>
                <div className="flex gap-1 items-center">
                  {/* Status dropdown for admin */}
                  {isAdmin ? (
                    <Select value={currentStatus} onValueChange={(v) => handleUpdateSuggestionStatus(sug.id, v)}>
                      <SelectTrigger className="h-6 text-[9px] w-[90px]">
                        <div className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${statusConfig.color}`} />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        {NOTE_STATUS_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex items-center gap-1">
                              <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                              <span className="text-[10px]">{opt.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className="text-[9px]">
                      <div className={`w-2 h-2 rounded-full ${statusConfig.color} mr-1`} />
                      {statusConfig.label}
                    </Badge>
                  )}
                  {/* Reply button for both roles */}
                  <Button variant="ghost" size="sm" className="h-5 text-[10px] px-2" onClick={() => setReplyingTo(replyingTo === sug.id ? null : sug.id)}>
                    <MessageSquare className="h-3 w-3 mr-1" /> Reply
                  </Button>
                </div>
              </div>
              
              {/* Suggestion Content */}
              <p className="text-xs whitespace-pre-wrap mb-2">{sug.content}</p>
              <p className="text-[10px] text-muted-foreground">Sent: {new Date(sug.created_at).toLocaleString()}</p>
              
              {/* Resolved Info */}
              {currentStatus === 'resolved' && sug.resolved_by && (
                <div className="mt-2 pt-2 border-t border-muted">
                  <p className="text-[10px] text-green-600">
                    ✓ Resolved by {sug.resolved_by} on {sug.resolved_at ? new Date(sug.resolved_at).toLocaleString() : 'N/A'}
                  </p>
                  {sug.resolve_note && (
                    <p className="text-xs mt-1 italic text-muted-foreground">"{sug.resolve_note}"</p>
                  )}
                </div>
              )}
              
              {/* Replies - show for both admin and leadership (chronological, newest at bottom) */}
              {sug.replies?.length > 0 && (
                <div className="mt-2 pt-2 border-t border-muted space-y-2">
                  {sug.replies.map(reply => {
                    const style = getReplyStyle(reply);
                    return (
                      <div key={reply.id} className={`${style.bg} p-2 rounded text-xs`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium ${style.labelColor}`}>
                            {reply.user_name} <span className="opacity-70">({style.label})</span>
                          </span>
                          <span className="text-[9px] text-muted-foreground">{new Date(reply.created_at).toLocaleString()}</span>
                        </div>
                        <p className="whitespace-pre-wrap">{reply.content}</p>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Reply Input - Available for unresolved OR resolved with admin reply for leadership */}
              {replyingTo === sug.id && (!sug.resolved || leadershipCanReply) && (
                <div className="mt-2 pt-2 border-t border-muted space-y-2">
                  <Textarea 
                    placeholder={isAdmin ? "Reply to this suggestion..." : "Reply to the dev team..."} 
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={2}
                    className="text-xs"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                      Cancel
                    </Button>
                    <Button size="sm" className="h-6 text-[10px]" onClick={() => handleAddReply(sug.id)} disabled={!replyText.trim()}>
                      <Send className="h-3 w-3 mr-1" /> Send Reply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              {isAdmin ? 'No feedback yet. Post an update below.' : 'No suggestions yet. Leave a note for the dev team below.'}
            </p>
          )}
        </div>
      </ScrollArea>
      
      {/* Both Admin and Leadership can add new suggestions/feedback */}
      {(isAdmin || isLeadership) && (
        <div className="space-y-2 mt-4 pt-4 border-t border-muted">
          <Textarea 
            placeholder={isAdmin ? "Add a note or update for leadership..." : "Leave a note or suggestion for the dev team..."} 
            value={newSuggestion} 
            onChange={(e) => setNewSuggestion(e.target.value)} 
            rows={2}
            className="text-sm"
          />
          <Button size="sm" onClick={handleAddSuggestion} disabled={!newSuggestion.trim()} className="w-full">
            <Send className="h-3 w-3 mr-1" /> {isAdmin ? 'Post Update' : 'Send to Dev Team'}
          </Button>
        </div>
      )}
    </div>
  );
};

// ==================== GOVERNANCE DETAIL DIALOGS ====================
const ScopeDialog = ({ pillar, open, onOpenChange, onSave, isAdmin, isLeadership, currentUser, onRefresh }) => {
  const [content, setContent] = useState(pillar?.content || {});
  const [saving, setSaving] = useState(false);
  const [newExclusion, setNewExclusion] = useState('');
  const [newTaskInputs, setNewTaskInputs] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  const [seenMarked, setSeenMarked] = useState(false);
  const [collapsedDeliverables, setCollapsedDeliverables] = useState({});
  const [collapsedTasks, setCollapsedTasks] = useState({}); // Default tasks section collapsed
  const [expandedTasks, setExpandedTasks] = useState({}); // Track which individual tasks are expanded
  const [allCollapsed, setAllCollapsed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [needsRefreshOnClose, setNeedsRefreshOnClose] = useState(false);

  useEffect(() => {
    if (pillar) setContent(pillar.content || {});
    setSeenMarked(false); // Reset when pillar changes
    setNeedsRefreshOnClose(false); // Reset refresh flag
  }, [pillar]);
  
  // Mark admin task updates as seen when leadership opens dialog (one time only)
  useEffect(() => {
    if (!open || !isLeadership || isAdmin || seenMarked) return;
    
    let needsUpdate = false;
    const userId = currentUser?.id;
    if (!userId) return;
    
    console.log('ScopeDialog: Checking for unseen admin updates for leadership user:', userId);
    
    let updatedContent = { ...pillar.content };
    
    // Handle legacy key_deliverables array structure
    if (pillar.content?.key_deliverables && Array.isArray(pillar.content.key_deliverables)) {
      const updatedDeliverables = pillar.content.key_deliverables.map(item => {
        const updatedTasks = (item.tasks || []).map(task => {
          let updatedTask = { ...task };
          
          // Mark admin updates as seen
          const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(userId));
          if (hasUnseenAdminUpdates) {
            needsUpdate = true;
            updatedTask.updates = task.updates.map(u => {
              if (u.is_admin && !u.seen_by?.includes(userId)) {
                return { ...u, seen_by: [...(u.seen_by || []), userId] };
              }
              return u;
            });
          }
          
          // Mark task completion as seen (if completed by admin and unseen)
          const completedByAdmin = task.completed && task.completed_by && !task.completion_seen_by?.includes(userId);
          if (completedByAdmin) {
            needsUpdate = true;
            updatedTask.completion_seen_by = [...(task.completion_seen_by || []), userId];
          }
          
          return hasUnseenAdminUpdates || completedByAdmin ? updatedTask : task;
        });
        return { ...item, tasks: updatedTasks };
      });
      updatedContent.key_deliverables = updatedDeliverables;
    }
    
    // Handle new phases object structure (for Scope)
    if (pillar.content?.phases && typeof pillar.content.phases === 'object') {
      const updatedPhases = {};
      Object.entries(pillar.content.phases).forEach(([phaseId, phaseItems]) => {
        if (Array.isArray(phaseItems)) {
          updatedPhases[phaseId] = phaseItems.map(item => {
            const updatedTasks = (item.tasks || []).map(task => {
              let updatedTask = { ...task };
              
              // Mark admin updates as seen
              const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(userId));
              if (hasUnseenAdminUpdates) {
                needsUpdate = true;
                updatedTask.updates = task.updates.map(u => {
                  if (u.is_admin && !u.seen_by?.includes(userId)) {
                    return { ...u, seen_by: [...(u.seen_by || []), userId] };
                  }
                  return u;
                });
              }
              
              // Mark task completion as seen (if completed by admin and unseen)
              const completedByAdmin = task.completed && task.completed_by && !task.completion_seen_by?.includes(userId);
              if (completedByAdmin) {
                needsUpdate = true;
                updatedTask.completion_seen_by = [...(task.completion_seen_by || []), userId];
              }
              
              return hasUnseenAdminUpdates || completedByAdmin ? updatedTask : task;
            });
            return { ...item, tasks: updatedTasks };
          });
        } else {
          updatedPhases[phaseId] = phaseItems;
        }
      });
      updatedContent.phases = updatedPhases;
    }
    
    if (needsUpdate) {
      console.log('ScopeDialog: Marking items as seen, will refresh on close');
      setSeenMarked(true);
      setNeedsRefreshOnClose(true); // Flag to refresh parent on close
      setContent(updatedContent);
      // Auto-save silently via direct API call
      const token = localStorage.getItem('access_token');
      axios.put(`${API}/api/governance/five-pillars/governance/${pillar.id}`, 
        { content: updatedContent },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(() => console.log('ScopeDialog: Seen status saved to backend'))
      .catch(err => console.log('Auto-save seen status failed:', err));
    } else {
      console.log('ScopeDialog: No unseen admin updates found');
    }
  }, [open, isLeadership, isAdmin, seenMarked, pillar, currentUser?.id]);
  
  // Mark suggestions with admin replies as seen when leadership opens dialog
  useEffect(() => {
    if (!open || !isLeadership || isAdmin || !pillar?.suggestions) return;
    
    const userId = currentUser?.id;
    if (!userId) return;
    
    // Check if any suggestions have admin replies that leadership hasn't seen
    const suggestionsToMark = pillar.suggestions.filter(s => {
      if (!s.replies || s.replies.length === 0) return false;
      const lastReply = s.replies[s.replies.length - 1];
      const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
      const hasSeen = s.seen_by && s.seen_by.includes(userId);
      return hasAdminReply && !hasSeen;
    });
    
    if (suggestionsToMark.length > 0) {
      console.log('Marking suggestions as seen');
      setNeedsRefreshOnClose(true); // Flag to refresh parent on close
      const updatedSuggestions = pillar.suggestions.map(s => {
        if (!s.replies || s.replies.length === 0) return s;
        const lastReply = s.replies[s.replies.length - 1];
        const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
        const hasSeen = s.seen_by && s.seen_by.includes(userId);
        if (hasAdminReply && !hasSeen) {
          return { ...s, seen_by: [...(s.seen_by || []), userId] };
        }
        return s;
      });
      
      // Save updated suggestions
      const token = localStorage.getItem('access_token');
      axios.put(`${API}/api/governance/five-pillars/governance/${pillar.id}`, 
        { suggestions: updatedSuggestions },
        { headers: { Authorization: `Bearer ${token}` } }
      ).then(() => console.log('Suggestions seen status saved'))
      .catch(err => console.log('Suggestions seen status save failed:', err));
    }
  }, [open, isLeadership, isAdmin, pillar?.suggestions, currentUser?.id]);

  const toggleExpanded = (idx) => {
    setExpandedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pillar.id, { content });
      toast.success('Scope updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Phase definitions - names can be customized and stored in content
  // Default phases - can be extended dynamically
  const DEFAULT_PHASE_IDS = ['phase1', 'phase2', 'phase3', 'phase4', 'phase5', 'phase6', 'phase7'];

  // Get all phase IDs (default + custom)
  const getPhaseIds = () => {
    const customPhaseIds = content.custom_phases || [];
    return [...DEFAULT_PHASE_IDS, ...customPhaseIds];
  };

  // Get phase name from content or generate default
  const getPhaseName = (phaseId) => {
    if (content.phase_names?.[phaseId]) {
      return content.phase_names[phaseId];
    }
    // Default naming for built-in phases
    const defaultIdx = DEFAULT_PHASE_IDS.indexOf(phaseId);
    if (defaultIdx !== -1) {
      return `Phase ${defaultIdx + 1}`;
    }
    // Custom phases - use stored name or ID
    return phaseId;
  };

  // Update phase name
  const updatePhaseName = (phaseId, newName) => {
    const phaseNames = { ...(content.phase_names || {}) };
    phaseNames[phaseId] = newName;
    setContent({ ...content, phase_names: phaseNames });
  };

  // Get phase status
  const getPhaseStatus = (phaseId) => {
    return content.phase_statuses?.[phaseId] || 'planning';
  };

  // Update phase status
  const updatePhaseStatus = (phaseId, newStatus) => {
    const phaseStatuses = { ...(content.phase_statuses || {}) };
    phaseStatuses[phaseId] = newStatus;
    setContent({ ...content, phase_statuses: phaseStatuses });
  };

  // Add new phase
  const addPhase = () => {
    const customPhases = [...(content.custom_phases || [])];
    const newPhaseId = `phase_${Date.now()}`;
    const totalPhases = DEFAULT_PHASE_IDS.length + customPhases.length + 1;
    const newPhaseName = `Phase ${totalPhases}`;
    
    customPhases.push(newPhaseId);
    const phaseNames = { ...(content.phase_names || {}), [newPhaseId]: newPhaseName };
    const updatedPhases = { ...phases, [newPhaseId]: [] };
    
    setContent({ 
      ...content, 
      custom_phases: customPhases, 
      phase_names: phaseNames,
      phases: updatedPhases
    });
    
    // Expand the new phase
    setCollapsedPhases(prev => ({ ...prev, [newPhaseId]: false }));
    toast.success(`Added "${newPhaseName}"`);
  };

  // Delete phase (only custom phases can be deleted)
  const deletePhase = (phaseId) => {
    // Can't delete default phases
    if (DEFAULT_PHASE_IDS.includes(phaseId)) {
      toast.error('Cannot delete default phases');
      return;
    }
    
    const phaseDeliverables = phases[phaseId] || [];
    if (phaseDeliverables.length > 0) {
      toast.error('Move or delete all deliverables before removing this phase');
      return;
    }
    
    const customPhases = (content.custom_phases || []).filter(id => id !== phaseId);
    const phaseNames = { ...(content.phase_names || {}) };
    delete phaseNames[phaseId];
    const updatedPhases = { ...phases };
    delete updatedPhases[phaseId];
    
    // Also remove sub-phases for this phase
    const subPhases = { ...(content.sub_phases || {}) };
    delete subPhases[phaseId];
    
    setContent({
      ...content,
      custom_phases: customPhases,
      phase_names: phaseNames,
      phases: updatedPhases,
      sub_phases: subPhases
    });
    
    toast.success('Phase removed');
  };

  // ==================== SUB-PHASE MANAGEMENT ====================
  // Get sub-phases for a phase
  const getSubPhases = (phaseId) => {
    return content.sub_phases?.[phaseId] || [];
  };

  // Add a new sub-phase to a phase
  const addSubPhase = (phaseId) => {
    const currentSubPhases = getSubPhases(phaseId);
    const subPhaseNumber = currentSubPhases.length + 1;
    const phaseName = getPhaseName(phaseId);
    const phaseIndex = getPhaseIds().indexOf(phaseId) + 1;
    
    const newSubPhase = {
      id: `sp_${Date.now()}`,
      name: `${phaseIndex}.${subPhaseNumber} Sub-phase`,
      status: 'planning',
      deliverables: []
    };
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: [...currentSubPhases, newSubPhase]
      }
    });
    
    toast.success(`Added sub-phase to ${phaseName}`);
  };

  // Update sub-phase name
  const updateSubPhaseName = (phaseId, subPhaseId, newName) => {
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => 
      sp.id === subPhaseId ? { ...sp, name: newName } : sp
    );
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Update sub-phase status
  const updateSubPhaseStatus = (phaseId, subPhaseId, newStatus) => {
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => 
      sp.id === subPhaseId ? { ...sp, status: newStatus } : sp
    );
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Delete sub-phase
  const deleteSubPhase = (phaseId, subPhaseId) => {
    const currentSubPhases = getSubPhases(phaseId);
    const subPhase = currentSubPhases.find(sp => sp.id === subPhaseId);
    
    if (subPhase?.deliverables?.length > 0) {
      toast.error('Move or delete all deliverables before removing this sub-phase');
      return;
    }
    
    const updatedSubPhases = currentSubPhases.filter(sp => sp.id !== subPhaseId);
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
    
    toast.success('Sub-phase removed');
  };

  // Add deliverable to sub-phase
  const addSubPhaseDeliverable = (phaseId, subPhaseId, name) => {
    if (!name.trim()) return;
    
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => {
      if (sp.id === subPhaseId) {
        return {
          ...sp,
          deliverables: [
            ...(sp.deliverables || []),
            {
              id: `d_${Date.now()}`,
              name: name.trim(),
              status: 'planning',
              tasks: []
            }
          ]
        };
      }
      return sp;
    });
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Update sub-phase deliverable
  const updateSubPhaseDeliverable = (phaseId, subPhaseId, deliverableIdx, field, value) => {
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => {
      if (sp.id === subPhaseId) {
        const updatedDeliverables = [...(sp.deliverables || [])];
        updatedDeliverables[deliverableIdx] = {
          ...updatedDeliverables[deliverableIdx],
          [field]: value
        };
        return { ...sp, deliverables: updatedDeliverables };
      }
      return sp;
    });
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Delete sub-phase deliverable
  const deleteSubPhaseDeliverable = (phaseId, subPhaseId, deliverableIdx) => {
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => {
      if (sp.id === subPhaseId) {
        const updatedDeliverables = [...(sp.deliverables || [])];
        updatedDeliverables.splice(deliverableIdx, 1);
        return { ...sp, deliverables: updatedDeliverables };
      }
      return sp;
    });
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Toggle sub-phase task
  const toggleSubPhaseTask = (phaseId, subPhaseId, deliverableIdx, taskIdx) => {
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => {
      if (sp.id === subPhaseId) {
        const updatedDeliverables = [...(sp.deliverables || [])];
        const tasks = [...(updatedDeliverables[deliverableIdx].tasks || [])];
        tasks[taskIdx] = { ...tasks[taskIdx], completed: !tasks[taskIdx].completed };
        updatedDeliverables[deliverableIdx] = { ...updatedDeliverables[deliverableIdx], tasks };
        return { ...sp, deliverables: updatedDeliverables };
      }
      return sp;
    });
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // Add task to sub-phase deliverable
  const addSubPhaseTask = (phaseId, subPhaseId, deliverableIdx, taskName) => {
    if (!taskName.trim()) return;
    
    const currentSubPhases = getSubPhases(phaseId);
    const updatedSubPhases = currentSubPhases.map(sp => {
      if (sp.id === subPhaseId) {
        const updatedDeliverables = [...(sp.deliverables || [])];
        const tasks = [...(updatedDeliverables[deliverableIdx].tasks || [])];
        tasks.push({
          id: `t_${Date.now()}`,
          name: taskName.trim(),
          completed: false
        });
        updatedDeliverables[deliverableIdx] = { ...updatedDeliverables[deliverableIdx], tasks };
        return { ...sp, deliverables: updatedDeliverables };
      }
      return sp;
    });
    
    setContent({
      ...content,
      sub_phases: {
        ...(content.sub_phases || {}),
        [phaseId]: updatedSubPhases
      }
    });
  };

  // State for collapsed sub-phases
  const [collapsedSubPhases, setCollapsedSubPhases] = useState({});
  const toggleSubPhaseCollapse = (subPhaseId) => {
    setCollapsedSubPhases(prev => ({ ...prev, [subPhaseId]: !prev[subPhaseId] }));
  };

  // State for new sub-phase deliverable inputs
  const [newSubPhaseDeliverableInputs, setNewSubPhaseDeliverableInputs] = useState({});
  
  // State for new sub-phase task inputs
  const [newSubPhaseTaskInputs, setNewSubPhaseTaskInputs] = useState({});

  // Build PHASES array with current names
  const PHASES = getPhaseIds().map(phaseId => ({
    id: phaseId,
    name: getPhaseName(phaseId),
    label: `${getPhaseName(phaseId)} Key Deliverables`,
    isCustom: !DEFAULT_PHASE_IDS.includes(phaseId),
  }));

  // Convert old format to new phase-based format
  const normalizePhases = (content) => {
    // If already has phases structure, use it
    if (content.phases && typeof content.phases === 'object') {
      const phases = {
        future_features: normalizeDeliverables(content.phases.future_features || []),
      };
      // Include all phase IDs (default + custom)
      getPhaseIds().forEach(phaseId => {
        phases[phaseId] = normalizeDeliverables(content.phases[phaseId] || []);
      });
      return phases;
    }
    // Migration: Move old key_deliverables to phase1
    const phases = {
      future_features: [],
      phase1: normalizeDeliverables(content.key_deliverables || []),
    };
    // Initialize default phases
    DEFAULT_PHASE_IDS.slice(1).forEach(phaseId => {
      phases[phaseId] = [];
    });
    return phases;
  };

  // Convert old string format to new object format if needed
  const normalizeDeliverables = (items) => {
    if (!items) return [];
    return items.map(item => {
      if (typeof item === 'string') {
        return { name: item, status: 'pending', due_date: '', progress: 0, tasks: [] };
      }
      return { ...item, tasks: item.tasks || [] };
    });
  };

  const phases = normalizePhases(content);

  // State for new deliverable input per phase
  const [newDeliverableInputs, setNewDeliverableInputs] = useState({});
  
  // State for collapsed phases - default ALL phases to collapsed (including future_features and exclusions)
  const getDefaultCollapsedState = () => {
    const collapsed = { future_features: true, exclusions: true };
    PHASES.forEach(p => { collapsed[p.id] = true; });
    return collapsed;
  };
  const [collapsedPhases, setCollapsedPhases] = useState(getDefaultCollapsedState);

  const addDeliverable = (phaseId) => {
    const inputValue = newDeliverableInputs[phaseId];
    if (!inputValue?.trim()) return;
    const newItem = { name: inputValue.trim(), status: 'pending', due_date: '', progress: 0, tasks: [] };
    const updatedPhases = { ...phases, [phaseId]: [...(phases[phaseId] || []), newItem] };
    setContent({ ...content, phases: updatedPhases });
    setNewDeliverableInputs(prev => ({ ...prev, [phaseId]: '' }));
  };

  const updateDeliverable = (phaseId, idx, field, value) => {
    const updated = [...(phases[phaseId] || [])];
    updated[idx] = { ...updated[idx], [field]: value };
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  const removeDeliverable = (phaseId, idx) => {
    const updated = (phases[phaseId] || []).filter((_, i) => i !== idx);
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  // Move deliverable to a different phase, resetting status to pending
  const moveDeliverable = (sourcePhaseId, idx, targetPhaseId) => {
    if (sourcePhaseId === targetPhaseId) return;
    
    // Use current phases from content
    const currentPhases = content.phases || {};
    const sourcePhaseItems = [...(currentPhases[sourcePhaseId] || [])];
    const [movedItem] = sourcePhaseItems.splice(idx, 1);
    
    // Reset status to pending when moved
    const itemWithPendingStatus = { ...movedItem, status: 'pending' };
    
    const targetPhaseItems = [...(currentPhases[targetPhaseId] || []), itemWithPendingStatus];
    
    setContent({ 
      ...content, 
      phases: { 
        ...currentPhases, 
        [sourcePhaseId]: sourcePhaseItems, 
        [targetPhaseId]: targetPhaseItems 
      } 
    });
    
    // Get phase name for toast
    const targetPhaseName = PHASES.find(p => p.id === targetPhaseId)?.name || 
                           (targetPhaseId === 'future_features' ? 'Future Features' : 
                            targetPhaseId === 'exclusions' ? 'Exclusions' : targetPhaseId);
    toast.success(`Moved to ${targetPhaseName}`);
  };

  // Drag and drop handler for reordering deliverables (within and across phases, including exclusions)
  const handleDragEnd = (result) => {
    console.log('Drag end result:', result);
    if (!result.destination) {
      console.log('No destination - drop cancelled');
      return;
    }
    
    const sourcePhase = result.source.droppableId;
    const destPhase = result.destination.droppableId;
    const sourceIndex = result.source.index;
    
    console.log(`Moving from ${sourcePhase}[${sourceIndex}] to ${destPhase}`);
    
    // Handle exclusions as source
    if (sourcePhase === 'exclusions') {
      const exclusionItems = getExclusionItems();
      const [movedItem] = exclusionItems.splice(sourceIndex, 1);
      
      if (destPhase === 'exclusions') {
        // Reorder within exclusions - use destination index
        const destIndex = result.destination.index;
        exclusionItems.splice(destIndex, 0, movedItem);
        setContent({ ...content, exclusions: exclusionItems });
      } else {
        // Move from exclusions to a phase - ADD TO BOTTOM of destination
        const destItems = Array.from(phases[destPhase] || []);
        const deliverable = {
          name: movedItem.name,
          status: 'pending',
          due_date: '',
          progress: 0,
          tasks: movedItem.notes?.map((note, idx) => ({
            id: note.id || Date.now() + idx,
            name: note.name || note.text,
            details: note.details || '',
            completed: false,
            assigned_by: note.assigned_by,
            assigned_at: note.assigned_at
          })) || []
        };
        // Add to bottom of the list
        destItems.push(deliverable);
        console.log(`Added to ${destPhase}, new length: ${destItems.length}`);
        setContent({ 
          ...content, 
          exclusions: exclusionItems,
          phases: { ...phases, [destPhase]: destItems } 
        });
      }
      return;
    }
    
    // Handle exclusions as destination (from phases)
    if (destPhase === 'exclusions') {
      const sourceItems = Array.from(phases[sourcePhase] || []);
      const [movedItem] = sourceItems.splice(sourceIndex, 1);
      const exclusionItems = getExclusionItems();
      
      // Convert deliverable to exclusion format - ADD TO BOTTOM
      const exclusion = {
        name: movedItem.name,
        description: movedItem.description || '',
        notes: movedItem.tasks?.map(task => ({
          id: task.id,
          name: task.name,
          details: task.details,
          assigned_by: task.assigned_by,
          assigned_at: task.assigned_at
        })) || []
      };
      // Add to bottom of exclusions
      exclusionItems.push(exclusion);
      
      setContent({ 
        ...content, 
        exclusions: exclusionItems,
        phases: { ...phases, [sourcePhase]: sourceItems } 
      });
      return;
    }
    
    // Same phase reorder
    if (sourcePhase === destPhase) {
      const items = Array.from(phases[sourcePhase] || []);
      const [reorderedItem] = items.splice(sourceIndex, 1);
      const destIndex = result.destination.index;
      items.splice(destIndex, 0, reorderedItem);
      setContent({ ...content, phases: { ...phases, [sourcePhase]: items } });
    } else {
      // Cross-phase move - ADD TO BOTTOM of destination
      const sourceItems = Array.from(phases[sourcePhase] || []);
      const destItems = Array.from(phases[destPhase] || []);
      const [movedItem] = sourceItems.splice(sourceIndex, 1);
      // Add to bottom
      destItems.push(movedItem);
      setContent({ 
        ...content, 
        phases: { 
          ...phases, 
          [sourcePhase]: sourceItems, 
          [destPhase]: destItems 
        } 
      });
    }
  };

  // Toggle collapse for a single deliverable (now includes phase)
  // Default is collapsed (undefined or true), expanded is explicitly false
  const toggleCollapse = (phaseId, idx) => {
    const key = `${phaseId}-${idx}`;
    setCollapsedDeliverables(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  // Toggle collapse for entire phase
  const togglePhaseCollapse = (phaseId) => {
    setCollapsedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  // Toggle all phases AND deliverables collapse state
  const toggleAllCollapse = () => {
    if (allCollapsed) {
      // Expand all - set all phases to expanded (not in object = expanded)
      // and all deliverables to explicitly false (expanded)
      setCollapsedPhases({});
      
      // Set all deliverables to false (expanded)
      const allDeliverablesExpanded = {};
      (phases.future_features || []).forEach((_, idx) => {
        allDeliverablesExpanded[`future_features-${idx}`] = false;
      });
      PHASES.forEach(phase => {
        (phases[phase.id] || []).forEach((_, idx) => { 
          allDeliverablesExpanded[`${phase.id}-${idx}`] = false; 
        });
      });
      (content.exclusions || []).forEach((_, idx) => {
        allDeliverablesExpanded[`exclusions-${idx}`] = false;
      });
      setCollapsedDeliverables(allDeliverablesExpanded);
    } else {
      // Collapse all phases including future_features and exclusions
      const allPhasesCollapsed = { future_features: true, exclusions: true };
      PHASES.forEach(phase => {
        allPhasesCollapsed[phase.id] = true;
      });
      setCollapsedPhases(allPhasesCollapsed);
      
      // Reset deliverables to default collapsed state (empty = all collapsed)
      setCollapsedDeliverables({});
    }
    setAllCollapsed(!allCollapsed);
  };

  // Calculate overall progress across all phases (excluding future_features as they're not assigned yet)
  const calculateOverallProgress = () => {
    let totalTasks = 0;
    let completedTasks = 0;
    PHASES.forEach(phase => {
      (phases[phase.id] || []).forEach(d => {
        if (d.tasks && d.tasks.length > 0) {
          totalTasks += d.tasks.length;
          completedTasks += d.tasks.filter(t => t.completed).length;
        }
      });
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  // Calculate phase progress (also works for future_features)
  const calculatePhaseProgress = (phaseId) => {
    let totalTasks = 0;
    let completedTasks = 0;
    // Count tasks from phase-level deliverables
    (phases[phaseId] || []).forEach(d => {
      if (d.tasks && d.tasks.length > 0) {
        totalTasks += d.tasks.length;
        completedTasks += d.tasks.filter(t => t.completed).length;
      }
    });
    // Count tasks from sub-phases
    const phaseSubPhases = content.sub_phases?.[phaseId] || [];
    phaseSubPhases.forEach(sp => {
      (sp.deliverables || []).forEach(d => {
        if (d.tasks && d.tasks.length > 0) {
          totalTasks += d.tasks.length;
          completedTasks += d.tasks.filter(t => t.completed).length;
        }
      });
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  // Get task counts for a phase (including sub-phases)
  const getPhaseTaskCounts = (phaseId) => {
    let totalTasks = 0;
    let completedTasks = 0;
    // Count tasks from phase-level deliverables
    (phases[phaseId] || []).forEach(d => {
      if (d.tasks && d.tasks.length > 0) {
        totalTasks += d.tasks.length;
        completedTasks += d.tasks.filter(t => t.completed).length;
      }
    });
    // Count tasks from sub-phases
    const phaseSubPhases = content.sub_phases?.[phaseId] || [];
    phaseSubPhases.forEach(sp => {
      (sp.deliverables || []).forEach(d => {
        if (d.tasks && d.tasks.length > 0) {
          totalTasks += d.tasks.length;
          completedTasks += d.tasks.filter(t => t.completed).length;
        }
      });
    });
    return { totalTasks, completedTasks };
  };

  const overallProgress = calculateOverallProgress();

  // Calculate deliverable progress from its tasks (source of truth)
  const getDeliverableProgress = (deliverable) => {
    const tasks = deliverable?.tasks || [];
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.completed).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  // Task management functions
  const [editingTask, setEditingTask] = useState(null); // {phaseId, deliverableIdx, taskId}
  const [taskForm, setTaskForm] = useState({ name: '', details: '', assignee: '', due_date: '' });

  const addTask = (phaseId, deliverableIdx, taskData) => {
    if (!taskData.name?.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const userRole = currentUser?.role || '';
    const newTask = { 
      id: Date.now(), 
      name: taskData.name.trim(), 
      details: taskData.details || '',
      assignee: taskData.assignee || '',
      due_date: taskData.due_date || '',
      completed: false,
      assigned_by: userName,
      assigned_by_id: currentUser?.id,
      created_by_role: userRole,
      assigned_at: new Date().toISOString(),
      updates: [],
      seen_by: [currentUser?.id] // Creator has seen it
    };
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = [...(phaseDeliverables[deliverableIdx].tasks || []), newTask];
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    // Auto-calculate progress
    const completedCount = tasks.filter(t => t.completed).length;
    phaseDeliverables[deliverableIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
    setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
    setExpandedItems(prev => ({ ...prev, [`${phaseId}-${deliverableIdx}`]: false }));
  };

  const updateTask = (phaseId, deliverableIdx, taskId, updates) => {
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = phaseDeliverables[deliverableIdx].tasks.map(t => 
      t.id === taskId ? { ...t, ...updates } : t
    );
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
  };

  const toggleTask = (phaseId, deliverableIdx, taskId) => {
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const task = phaseDeliverables[deliverableIdx].tasks.find(t => t.id === taskId);
    const completedBy = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const tasks = phaseDeliverables[deliverableIdx].tasks.map(t => 
      t.id === taskId ? { 
        ...t, 
        completed: !t.completed,
        completed_by: !t.completed ? completedBy : null,
        completed_at: !t.completed ? new Date().toISOString() : null
      } : t
    );
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    // Auto-calculate progress
    const completedCount = tasks.filter(t => t.completed).length;
    phaseDeliverables[deliverableIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    // Auto-update status based on progress
    if (phaseDeliverables[deliverableIdx].progress === 100) {
      phaseDeliverables[deliverableIdx].status = 'completed';
    } else if (phaseDeliverables[deliverableIdx].progress > 0) {
      phaseDeliverables[deliverableIdx].status = 'in_progress';
    }
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
  };

  const removeTask = (phaseId, deliverableIdx, taskId) => {
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = phaseDeliverables[deliverableIdx].tasks.filter(t => t.id !== taskId);
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    // Auto-calculate progress
    const completedCount = tasks.filter(t => t.completed).length;
    phaseDeliverables[deliverableIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
  };

  // Move task up in the list
  const moveTaskUp = (phaseId, deliverableIdx, taskIdx) => {
    if (taskIdx === 0) return; // Already at top
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = [...phaseDeliverables[deliverableIdx].tasks];
    [tasks[taskIdx - 1], tasks[taskIdx]] = [tasks[taskIdx], tasks[taskIdx - 1]];
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
  };

  // Move task down in the list
  const moveTaskDown = (phaseId, deliverableIdx, taskIdx) => {
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = [...phaseDeliverables[deliverableIdx].tasks];
    if (taskIdx >= tasks.length - 1) return; // Already at bottom
    [tasks[taskIdx], tasks[taskIdx + 1]] = [tasks[taskIdx + 1], tasks[taskIdx]];
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
  };

  // Task update functionality
  const [showUpdateForm, setShowUpdateForm] = useState(null); // { phaseId, deliverableIdx, taskId }
  const [updateNote, setUpdateNote] = useState('');
  
  const addTaskUpdate = (phaseId, deliverableIdx, taskId) => {
    if (!updateNote.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'User';
    const userRole = currentUser?.role || '';
    const isAdminUser = ['super_admin', 'admin'].includes(userRole);
    
    const newUpdate = {
      id: Date.now(),
      note: updateNote.trim(),
      user_name: userName,
      user_id: currentUser?.id,
      user_role: userRole,
      is_admin: isAdminUser,
      created_at: new Date().toISOString(),
      seen_by: [currentUser?.id] // Creator has seen it
    };
    
    const phaseDeliverables = [...(phases[phaseId] || [])];
    const tasks = phaseDeliverables[deliverableIdx].tasks.map(t => {
      if (t.id === taskId) {
        return { ...t, updates: [...(t.updates || []), newUpdate] };
      }
      return t;
    });
    phaseDeliverables[deliverableIdx] = { ...phaseDeliverables[deliverableIdx], tasks };
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseDeliverables } });
    setUpdateNote('');
    setShowUpdateForm(null);
    toast.success('Update added');
  };

  // Core team members for quick assignment
  const [platformUsers, setPlatformUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(null); // { phaseId, deliverableIdx }
  
  // Default team members
  const defaultTeamMembers = [
    { id: 'ryan_march', name: 'Ryan March', role: 'Lead Developer', avatar: 'RM' },
    { id: 'ryan_adams', name: 'Ryan Adams', role: 'Developer', avatar: 'RA' },
    { id: 'gabe', name: 'Gabriel Gecan', role: 'Developer', avatar: 'GG' },
  ];
  
  // Fetch platform users
  const fetchPlatformUsers = async () => {
    if (platformUsers.length > 0) return;
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = response.data.map(u => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        role: u.role?.replace(/_/g, ' '),
        avatar: `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()
      }));
      setPlatformUsers(users);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };
  
  // Combined team members (default + platform users)
  const allTeamMembers = [...defaultTeamMembers, ...platformUsers.filter(pu => 
    !defaultTeamMembers.find(dm => dm.name.toLowerCase() === pu.name.toLowerCase())
  )];

  const addExclusion = () => {
    if (!newExclusion.trim()) return;
    setContent({ ...content, exclusions: [...(content.exclusions || []), newExclusion.trim()] });
    setNewExclusion('');
  };

  // Exclusions management - new interactive format
  // Helper to get exclusion items as objects (handles legacy string format)
  const getExclusionItems = () => {
    const exclusions = content.exclusions || [];
    return exclusions.map(item => {
      if (typeof item === 'string') {
        return { name: item, description: '', notes: [] };
      }
      return { ...item, notes: item.notes || [] };
    });
  };

  // Get count for badge
  const getExclusionsCount = () => {
    return (content.exclusions || []).length;
  };

  // Add new exclusion item
  const addExclusionItem = () => {
    if (!newExclusion.trim()) return;
    const newItem = { name: newExclusion.trim(), description: '', notes: [] };
    setContent({ ...content, exclusions: [...(content.exclusions || []), newItem] });
    setNewExclusion('');
  };

  // Update exclusion item field
  const updateExclusionItem = (idx, field, value) => {
    const items = getExclusionItems();
    items[idx] = { ...items[idx], [field]: value };
    setContent({ ...content, exclusions: items });
  };

  // Remove exclusion item
  const removeExclusionItem = (idx) => {
    const items = getExclusionItems().filter((_, i) => i !== idx);
    setContent({ ...content, exclusions: items });
  };

  // Add note to exclusion
  const addExclusionNote = (exclusionIdx, noteData) => {
    const items = getExclusionItems();
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'User';
    const newNote = {
      id: Date.now(),
      name: noteData.name?.trim() || '',
      details: noteData.details || '',
      assigned_by: userName,
      assigned_at: new Date().toISOString()
    };
    items[exclusionIdx] = {
      ...items[exclusionIdx],
      notes: [...(items[exclusionIdx].notes || []), newNote]
    };
    setContent({ ...content, exclusions: items });
    setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
  };

  // Remove note from exclusion
  const removeExclusionNote = (exclusionIdx, noteIdx) => {
    const items = getExclusionItems();
    items[exclusionIdx] = {
      ...items[exclusionIdx],
      notes: (items[exclusionIdx].notes || []).filter((_, i) => i !== noteIdx)
    };
    setContent({ ...content, exclusions: items });
  };

  // Move exclusion item to a phase (convert exclusion to deliverable)
  const moveExclusionToPhase = (exclusionIdx, targetPhaseId) => {
    const exclusionItems = getExclusionItems();
    const [movedExclusion] = exclusionItems.splice(exclusionIdx, 1);
    
    // Convert exclusion to deliverable format with pending status
    const newDeliverable = {
      name: movedExclusion.name,
      status: 'pending',
      due_date: '',
      progress: 0,
      tasks: [],
      // Preserve any notes as description
      description: movedExclusion.description || ''
    };
    
    // Use current phases from content, not the derived `phases` variable
    const currentPhases = content.phases || {};
    const targetPhaseItems = [...(currentPhases[targetPhaseId] || []), newDeliverable];
    
    setContent({ 
      ...content, 
      exclusions: exclusionItems,
      phases: { 
        ...currentPhases, 
        [targetPhaseId]: targetPhaseItems 
      } 
    });
    
    // Get phase name for toast
    const targetPhaseName = PHASES.find(p => p.id === targetPhaseId)?.name || 
                           (targetPhaseId === 'future_features' ? 'Future Features' : targetPhaseId);
    toast.success(`Moved "${movedExclusion.name}" to ${targetPhaseName}`);
  };

  // Deliverable status options for Scope
  const statusOptions = [
    { value: 'planning', label: 'Planning', color: 'bg-slate-500' },
    { value: 'design', label: 'Design', color: 'bg-purple-500' },
    { value: 'building', label: 'Building', color: 'bg-blue-400' },
    { value: 'coding', label: 'Coding', color: 'bg-blue-600' },
    { value: 'internal_testing', label: 'Internal Testing', color: 'bg-cyan-500' },
    { value: 'team_testing', label: 'Team Testing', color: 'bg-teal-500' },
    { value: 'debug', label: 'De-Bug', color: 'bg-orange-500' },
    { value: 'edit_upgrades', label: 'Edit/Upgrades', color: 'bg-amber-500' },
    { value: 'final_testing', label: 'Final Testing', color: 'bg-indigo-500' },
    { value: 'release_ready', label: 'Release Ready', color: 'bg-emerald-500' },
    { value: 'needs_attention', label: 'Needs Attention', color: 'bg-red-500' },
    { value: 'completed', label: 'Completed', color: 'bg-green-500' },
  ];

  // Helper to count total deliverables across all phases (excluding future features)
  const getTotalDeliverables = () => {
    return PHASES.reduce((sum, phase) => sum + (phases[phase.id]?.length || 0), 0);
  };

  // Helper to count future features
  const getFutureFeatureCount = () => {
    return phases.future_features?.length || 0;
  };

  // Handle dialog close with potential refresh
  const handleOpenChange = (isOpen) => {
    console.log('ScopeDialog: handleOpenChange called, isOpen:', isOpen, 'needsRefreshOnClose:', needsRefreshOnClose);
    if (!isOpen && needsRefreshOnClose && onRefresh) {
      console.log('ScopeDialog: Triggering parent refresh');
      // Trigger refresh after dialog closes
      setTimeout(() => {
        console.log('ScopeDialog: Calling onRefresh now');
        onRefresh();
      }, 100);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] overflow-y-auto"
        data-scope-dialog-content
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-500" /> Scope
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Define project scope, deliverables, and exclusions</span>
            <span className="text-xs font-medium">Overall Progress: <span className="text-indigo-600">{overallProgress}%</span></span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Summary</Label>
            <Textarea 
              value={content.summary || ''} 
              onChange={(e) => setContent({...content, summary: e.target.value})}
              rows={3} disabled={!isAdmin} className="mt-1"
            />
          </div>
          
          {/* Global controls */}
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              All Phases
              <Badge variant="outline" className="text-[10px]">{PHASES.length} phases</Badge>
              <Badge variant="outline" className="text-[10px]">{getTotalDeliverables()} deliverables</Badge>
            </Label>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-6 text-[10px] px-2"
                  onClick={addPhase}
                  data-testid="add-phase-btn"
                >
                  <Plus className="h-3 w-3 mr-1" /> Add Phase
                </Button>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 text-[10px] px-2"
                onClick={toggleAllCollapse}
              >
                {allCollapsed ? <Maximize2 className="h-3 w-3 mr-1" /> : <Minimize2 className="h-3 w-3 mr-1" />}
                {allCollapsed ? 'Expand All' : 'Collapse All'}
              </Button>
              <Progress value={overallProgress} className="w-24 h-2" />
            </div>
          </div>
          
          {/* Multi-phase DragDropContext */}
          <DragDropContext 
            onBeforeCapture={() => {
              document.documentElement.classList.add('dragging-active');
            }}
            onDragStart={(start) => {
              setIsDragging(true);
              console.log('Drag started:', start.draggableId, 'from', start.source.droppableId);
            }}
            onDragEnd={(result) => {
              setIsDragging(false);
              document.documentElement.classList.remove('dragging-active');
              handleDragEnd(result);
            }}
          >
            <style>{`
              /* Prevent body scroll during drag */
              .dragging-active,
              .dragging-active body {
                overflow: hidden !important;
              }
              
              /* Style for the dragging item - keep it controlled */
              [data-rbd-draggable-id][data-rbd-drag-handle-draggable-id] {
                /* Item follows cursor properly */
              }
              
              /* Highlight drop zones strongly when dragging over */
              [data-rbd-droppable-id] {
                transition: all 0.2s ease;
              }
              
              /* Strong highlight for phases when item is dragged over */
              .phase-drop-zone-active {
                background-color: rgba(99, 102, 241, 0.3) !important;
                border: 3px dashed rgb(99, 102, 241) !important;
                border-radius: 8px;
              }
              
              /* Strong highlight for future features */
              .future-drop-zone-active {
                background-color: rgba(59, 130, 246, 0.3) !important;
                border: 3px dashed rgb(59, 130, 246) !important;
                border-radius: 8px;
              }
              
              /* Strong highlight for exclusions */
              .exclusions-drop-zone-active {
                background-color: rgba(239, 68, 68, 0.3) !important;
                border: 3px dashed rgb(239, 68, 68) !important;
                border-radius: 8px;
              }
            `}</style>
            <div 
              className="space-y-4"
              style={isDragging ? { touchAction: 'none', userSelect: 'none' } : {}}
            >
              {PHASES.map((phase) => {
                const phaseDeliverables = phases[phase.id] || [];
                const phaseProgress = calculatePhaseProgress(phase.id);
                const phaseTaskCounts = getPhaseTaskCounts(phase.id);
                const isPhaseCollapsed = collapsedPhases[phase.id];
                
                return (
                  <Droppable key={phase.id} droppableId={phase.id}>
                    {(droppableProvided, droppableSnapshot) => (
                      <div 
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                        className={`border rounded-lg overflow-hidden transition-all duration-200 ${
                          droppableSnapshot.isDraggingOver 
                            ? 'ring-4 ring-indigo-500 bg-indigo-500/20 border-indigo-500' 
                            : ''
                        }`}
                      >
                        {/* Phase Header - clicking toggles collapse, but also acts as drop zone */}
                        <div 
                          className={`flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-all ${
                            droppableSnapshot.isDraggingOver ? 'bg-indigo-500/30' : 'bg-muted/30'
                          }`}
                          onClick={() => togglePhaseCollapse(phase.id)}
                        >
                          <div className="flex items-center gap-2 flex-1 flex-wrap">
                            {isPhaseCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusOptions.find(s => s.value === getPhaseStatus(phase.id))?.color || 'bg-slate-500'}`} />
                            {isAdmin ? (
                              <Input 
                                value={phase.name}
                                onChange={(e) => updatePhaseName(phase.id, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                className="h-7 font-semibold text-sm w-[150px]"
                                placeholder="Phase name..."
                              />
                            ) : (
                              <Label className="cursor-pointer font-semibold">{phase.name}</Label>
                            )}
                            {isAdmin ? (
                              <Select 
                                value={getPhaseStatus(phase.id)} 
                                onValueChange={(v) => updatePhaseStatus(phase.id, v)}
                              >
                                <SelectTrigger 
                                  className="h-7 text-xs w-[130px]" 
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {statusOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>
                                      <div className="flex items-center gap-2">
                                        <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                                        {opt.label}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="outline" className="text-[10px]">
                                {statusOptions.find(s => s.value === getPhaseStatus(phase.id))?.label || 'Planning'}
                              </Badge>
                            )}
                            {/* Phase Due Date */}
                            {isAdmin ? (
                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                <Calendar className="h-3 w-3 text-muted-foreground" />
                                <Input 
                                  type="date" 
                                  value={content.phase_due_dates?.[phase.id] || ''} 
                                  onChange={(e) => setContent({ ...content, phase_due_dates: { ...(content.phase_due_dates || {}), [phase.id]: e.target.value } })}
                                  className="h-7 text-xs w-[120px]" 
                                />
                              </div>
                            ) : content.phase_due_dates?.[phase.id] && (
                              <span className="text-xs text-muted-foreground">Due: {new Date(content.phase_due_dates[phase.id]).toLocaleDateString()}</span>
                            )}
                            <Badge variant="secondary" className="text-[10px]">{phaseDeliverables.length} deliverables</Badge>
                            <Badge variant="outline" className={`text-[10px] ${phaseTaskCounts.completedTasks > 0 ? 'border-green-500/50 text-green-600' : ''}`}>
                              {phaseTaskCounts.completedTasks}/{phaseTaskCounts.totalTasks} tasks
                            </Badge>
                            <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-400/50">{getSubPhases(phase.id).length} sub-phases</Badge>
                            {phase.isCustom && (
                              <Badge variant="outline" className="text-[9px] text-purple-400 border-purple-400/50">Custom</Badge>
                            )}
                            {droppableSnapshot.isDraggingOver && (
                              <span className="text-xs text-indigo-400 font-semibold animate-pulse ml-2">⬇️ Drop here!</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-muted-foreground">{phaseProgress}%</span>
                            <Progress value={phaseProgress} className="w-16 h-1.5" />
                            {/* Add Sub-phase button */}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-cyan-400 hover:text-cyan-500 hover:bg-cyan-500/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addSubPhase(phase.id);
                                }}
                                title="Add sub-phase"
                              >
                                <Plus className="h-3 w-3" />
                              </Button>
                            )}
                            {/* Delete button for custom phases (only when empty) */}
                            {isAdmin && phase.isCustom && phaseDeliverables.length === 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deletePhase(phase.id);
                                }}
                                title="Delete this phase"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Phase Content - always in DOM but visually collapsed */}
                        <div 
                          className="p-3 pt-0 transition-all duration-200"
                          style={{ 
                            maxHeight: isPhaseCollapsed ? '0px' : '2000px',
                            overflow: isPhaseCollapsed ? 'hidden' : 'visible',
                            opacity: isPhaseCollapsed ? 0 : 1,
                            padding: isPhaseCollapsed ? '0 12px' : undefined
                          }}
                        >
                          <div className="space-y-2 mt-3 p-2 rounded-lg">
                            {!isPhaseCollapsed && phaseDeliverables.length === 0 && !droppableSnapshot.isDraggingOver && (
                              <div className="text-center py-4 text-xs text-muted-foreground italic">
                                No deliverables in this phase. Drag items here or add new ones.
                              </div>
                            )}
                            {!isPhaseCollapsed && phaseDeliverables.map((item, i) => {
                                const collapseKey = `${phase.id}-${i}`;
                                return (
                                  <Draggable key={`${phase.id}-${i}`} draggableId={`${phase.id}-${i}`} index={i} isDragDisabled={!isAdmin && !isLeadership}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        style={provided.draggableProps.style}
                                        className={`rounded-lg border ${
                                          item.status === 'completed' ? 'bg-green-500/10 border-green-500/30' :
                                          item.status === 'in_progress' ? 'bg-amber-500/10 border-amber-500/30' :
                                          item.status === 'blocked' ? 'bg-red-500/10 border-red-500/30' :
                                          'bg-muted/50'
                                        } ${snapshot.isDragging ? 'shadow-2xl ring-4 ring-indigo-500 z-[9999] opacity-90' : ''}`}
                                      >
                                        {/* Deliverable Header - always visible */}
                                        <div className="flex items-center gap-2 p-3 cursor-pointer select-none" onClick={() => !snapshot.isDragging && toggleCollapse(phase.id, i)}>
                                          {(isAdmin || isLeadership) && (
                                            <div 
                                              {...provided.dragHandleProps} 
                                              className="cursor-grab active:cursor-grabbing hover:bg-muted rounded p-1 touch-none"
                                              style={{ touchAction: 'none' }}
                                              onClick={(e) => e.stopPropagation()}
                                              onMouseDown={(e) => e.stopPropagation()}
                                              onTouchStart={(e) => e.stopPropagation()}
                                            >
                                              <GripVertical className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                          )}
                                          <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusOptions.find(s => s.value === item.status)?.color || 'bg-slate-500'}`} />
                                          <Badge 
                                            variant="outline" 
                                            className={`text-[9px] px-1.5 py-0 h-5 flex-shrink-0 ${
                                              item.status === 'completed' ? 'border-green-500/50 text-green-600 bg-green-500/10' :
                                              item.status === 'coding' ? 'border-blue-500/50 text-blue-600 bg-blue-500/10' :
                                              item.status === 'internal_testing' || item.status === 'team_testing' || item.status === 'final_testing' ? 'border-cyan-500/50 text-cyan-600 bg-cyan-500/10' :
                                              item.status === 'needs_attention' ? 'border-red-500/50 text-red-600 bg-red-500/10' :
                                              item.status === 'release_ready' ? 'border-emerald-500/50 text-emerald-600 bg-emerald-500/10' :
                                              'border-slate-500/30 text-slate-500'
                                            }`}
                                          >
                                            {statusOptions.find(s => s.value === item.status)?.label || 'Planning'}
                                          </Badge>
                                          <span className="font-medium text-sm flex-1 truncate">{item.name || 'Untitled Deliverable'}</span>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            {/* Start date */}
                                            {isAdmin ? (
                                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <span className="text-[9px] text-muted-foreground">Start:</span>
                                                <Input 
                                                  type="date" 
                                                  value={item.start_date || ''} 
                                                  onChange={(e) => updateDeliverable(phase.id, i, 'start_date', e.target.value)}
                                                  className="h-6 text-[10px] w-[100px]"
                                                />
                                              </div>
                                            ) : (
                                              <div className="text-center w-16">
                                                <div className="text-[9px] text-muted-foreground uppercase">Start</div>
                                                <div className="text-[10px] font-medium">
                                                  {item.start_date ? new Date(item.start_date).toLocaleDateString('en-US', {month:'short', day:'numeric'}) : '—'}
                                                </div>
                                              </div>
                                            )}
                                            {/* Target date (was due date) */}
                                            {isAdmin ? (
                                              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                <span className="text-[9px] text-muted-foreground">Target:</span>
                                                <Input 
                                                  type="date" 
                                                  value={item.due_date || ''} 
                                                  onChange={(e) => updateDeliverable(phase.id, i, 'due_date', e.target.value)}
                                                  className={`h-6 text-[10px] w-[100px] ${item.due_date && new Date(item.due_date) < new Date() && item.status !== 'completed' ? 'border-red-500/50 text-red-600' : ''}`}
                                                />
                                              </div>
                                            ) : (
                                              <div className="text-center w-16">
                                                <div className="text-[9px] text-muted-foreground uppercase">Target</div>
                                                <div className={`text-[10px] font-medium ${item.due_date && new Date(item.due_date) < new Date() && item.status !== 'completed' ? 'text-red-500' : ''}`}>
                                                  {item.due_date ? new Date(item.due_date).toLocaleDateString('en-US', {month:'short', day:'numeric'}) : '—'}
                                                </div>
                                              </div>
                                            )}
                                            {/* Task count badge */}
                                            <Badge variant="outline" className={`text-[10px] ${(item.tasks?.filter(t => t.completed).length || 0) > 0 ? 'border-green-500/50 text-green-600' : ''}`}>
                                              {item.tasks?.filter(t => t.completed).length || 0}/{item.tasks?.length || 0} tasks
                                            </Badge>
                                            <span className="text-xs font-bold text-muted-foreground">{getDeliverableProgress(item)}%</span>
                                            {collapsedDeliverables[collapseKey] === false ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                          </div>
                                        </div>
                                        
                                        {/* Deliverable Collapsible content */}
                                        {collapsedDeliverables[collapseKey] === false && (
                                          <div className="px-3 pb-3 pt-0 space-y-2 border-t">
                                            <div className="flex items-center justify-between gap-2 pt-2">
                                              {isAdmin ? (
                                                <Input 
                                                  value={item.name} 
                                                  onChange={(e) => updateDeliverable(phase.id, i, 'name', e.target.value)}
                                                  className="h-8 font-medium flex-1"
                                                  placeholder="Deliverable name..."
                                                  onClick={(e) => e.stopPropagation()}
                                                />
                                              ) : (
                                                <span className="font-medium text-sm">{item.name}</span>
                                              )}
                                              <div className="flex items-center gap-1 flex-shrink-0">
                                                {/* Move to Phase dropdown */}
                                                {(isAdmin || isLeadership) && (
                                                  <Select 
                                                    value="" 
                                                    onValueChange={(targetPhaseId) => {
                                                      moveDeliverable(phase.id, i, targetPhaseId);
                                                    }}
                                                  >
                                                    <SelectTrigger 
                                                      className="h-7 w-[130px] text-xs"
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      <ArrowRightLeft className="h-3 w-3 mr-1" />
                                                      <span>Move to...</span>
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      {PHASES.filter(p => p.id !== phase.id).map(p => (
                                                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                      ))}
                                                      <SelectItem value="future_features">Future Features</SelectItem>
                                                      <SelectItem value="exclusions">Exclusions</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                )}
                                                {isAdmin && (
                                                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={(e) => { e.stopPropagation(); removeDeliverable(phase.id, i); }}>
                                                    <X className="h-3 w-3" />
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                  
                                            {/* Status, Start Date, Target Date, Progress row */}
                                            <div className="flex items-center gap-3 flex-wrap">
                                              {isAdmin ? (
                                                <>
                                                  <Select value={item.status || 'pending'} onValueChange={(v) => updateDeliverable(phase.id, i, 'status', v)}>
                                                    <SelectTrigger className="h-7 text-xs w-[140px]">
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      {statusOptions.map(opt => (
                                                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                                      ))}
                                                    </SelectContent>
                                                  </Select>
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-[10px] text-muted-foreground">Start:</span>
                                                    <Input type="date" value={item.start_date || ''} onChange={(e) => updateDeliverable(phase.id, i, 'start_date', e.target.value)} className="h-7 text-xs w-[120px]" />
                                                  </div>
                                                  <div className="flex items-center gap-1">
                                                    <span className="text-[10px] text-muted-foreground">Target:</span>
                                                    <Input type="date" value={item.due_date || ''} onChange={(e) => updateDeliverable(phase.id, i, 'due_date', e.target.value)} className="h-7 text-xs w-[120px]" />
                                                  </div>
                                                  <div className="flex items-center gap-1 ml-auto">
                                                    <span className="text-xs text-muted-foreground">Progress:</span>
                                                    <span className="text-xs font-bold">{getDeliverableProgress(item)}%</span>
                                                    <span className="text-[10px] text-muted-foreground">({item.tasks?.filter(t => t.completed).length || 0}/{item.tasks?.length || 0} tasks)</span>
                                                  </div>
                                                </>
                                              ) : (
                                                <>
                                                  <Badge variant="outline" className="text-[10px]">{statusOptions.find(s => s.value === item.status)?.label || 'Planning'}</Badge>
                                                  {item.start_date && <span className="text-xs text-muted-foreground">Start: {new Date(item.start_date).toLocaleDateString()}</span>}
                                                  {item.due_date && <span className="text-xs text-muted-foreground">Target: {new Date(item.due_date).toLocaleDateString()}</span>}
                                                  <span className="text-xs font-bold ml-auto">{getDeliverableProgress(item)}%</span>
                                                </>
                                              )}
                                            </div>
                                  
                                            {/* Progress bar */}
                                            <Progress value={getDeliverableProgress(item)} className="h-1.5" />
                                            
                                            {/* Tasks section */}
                                            <div className="mt-3 pt-3 border-t">
                                              <div 
                                                className="flex items-center justify-between mb-2 cursor-pointer select-none"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  const taskKey = `${phase.id}-${i}-tasks`;
                                                  setCollapsedTasks(prev => ({ ...prev, [taskKey]: prev[taskKey] === false ? true : false }));
                                                }}
                                              >
                                                <Label className="text-xs text-muted-foreground flex items-center gap-2 cursor-pointer">
                                                  {collapsedTasks[`${phase.id}-${i}-tasks`] === false ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                                  Tasks
                                                  <Badge variant="secondary" className="text-[10px]">
                                                    {item.tasks?.filter(t => t.completed).length || 0}/{item.tasks?.length || 0}
                                                  </Badge>
                                                </Label>
                                                {(isAdmin || isLeadership) && (
                                                  <Button 
                                                    size="sm" 
                                                    variant="outline" 
                                                    className="h-6 text-[10px] px-2"
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setShowTaskForm(showTaskForm?.phaseId === phase.id && showTaskForm?.deliverableIdx === i ? null : { phaseId: phase.id, deliverableIdx: i });
                                                      setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                                      fetchPlatformUsers();
                                                      // Expand tasks when adding
                                                      setCollapsedTasks(prev => ({ ...prev, [`${phase.id}-${i}-tasks`]: false }));
                                                    }}
                                                  >
                                                    <Plus className="h-3 w-3 mr-1" /> Add Task
                                                  </Button>
                                                )}
                                              </div>
                                              
                                              {/* Task list - collapsible */}
                                              {collapsedTasks[`${phase.id}-${i}-tasks`] === false && (
                                              <div className="space-y-2">
                                                {item.tasks?.map((task, taskIdx) => {
                                                  const assigneeInfo = allTeamMembers.find(m => m.id === task.assignee || m.name === task.assignee);
                                                  return (
                                                    <div key={task.id} className={`rounded-lg border ${task.completed ? 'bg-green-500/10 border-green-500/30' : 'bg-muted/30 border-border'}`}>
                                                      {/* Task Header - Always Visible */}
                                                      <div 
                                                        className="flex items-center justify-between p-2 cursor-pointer select-none"
                                                        onClick={() => setExpandedTasks(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                                                      >
                                                        <div className="flex items-center gap-2 flex-1 min-w-0">
                                                          {expandedTasks[task.id] ? <ChevronDown className="h-3 w-3 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 flex-shrink-0" />}
                                                          <span className={`text-sm font-medium truncate ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                                            {task.name}
                                                          </span>
                                                          {task.completed && <Badge className="bg-green-500/20 text-green-700 text-[9px] flex-shrink-0">✓</Badge>}
                                                          {task.updates?.length > 0 && (
                                                            <Badge variant="outline" className="text-[9px] flex-shrink-0">
                                                              <MessageSquare className="h-2.5 w-2.5 mr-1" />{task.updates.length}
                                                            </Badge>
                                                          )}
                                                        </div>
                                                        <div className="flex items-center gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                                          {(isAdmin || isLeadership) && (
                                                            <>
                                                              {/* Reorder buttons */}
                                                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveTaskUp(phase.id, i, taskIdx)} disabled={taskIdx === 0}>
                                                                <ChevronUp className="h-3 w-3" />
                                                              </Button>
                                                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveTaskDown(phase.id, i, taskIdx)} disabled={taskIdx === item.tasks.length - 1}>
                                                                <ChevronDown className="h-3 w-3" />
                                                              </Button>
                                                              <Button size="sm" variant={task.completed ? "secondary" : "outline"} className={`h-6 text-[9px] px-2 ${task.completed ? 'bg-green-500/20 text-green-700' : ''}`} onClick={() => toggleTask(phase.id, i, task.id)}>
                                                                {task.completed ? <CheckCircle className="h-3 w-3" /> : 'Complete'}
                                                              </Button>
                                                              <Button size="sm" variant="outline" className="h-6 text-[9px] px-2" onClick={() => {
                                                                setShowUpdateForm(showUpdateForm?.taskId === task.id ? null : { phaseId: phase.id, deliverableIdx: i, taskId: task.id });
                                                                setExpandedTasks(prev => ({ ...prev, [task.id]: true }));
                                                              }}>
                                                                <Pencil className="h-3 w-3" />
                                                              </Button>
                                                              <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-500" onClick={() => removeTask(phase.id, i, task.id)}>
                                                                <X className="h-3 w-3" />
                                                              </Button>
                                                            </>
                                                          )}
                                                        </div>
                                                      </div>
                                                      
                                                      {/* Task Expanded Content */}
                                                      {expandedTasks[task.id] && (
                                                        <div className="px-3 pb-3 pt-0 space-y-2 border-t">
                                                          {task.details && <p className="text-xs text-muted-foreground pt-2">{task.details}</p>}
                                                          <div className="flex items-center gap-3 flex-wrap text-[10px] pt-1">
                                                            {(task.assignee || assigneeInfo) && (
                                                              <div className="flex items-center gap-1.5 bg-blue-500/10 px-2 py-1 rounded-full">
                                                                <Avatar className="h-4 w-4">
                                                                  <AvatarFallback className="text-[8px] bg-blue-500 text-white">
                                                                    {assigneeInfo?.avatar || task.assignee?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                                                                  </AvatarFallback>
                                                                </Avatar>
                                                                <span className="text-blue-700 font-medium">{assigneeInfo?.name || task.assignee}</span>
                                                              </div>
                                                            )}
                                                            {task.due_date && (
                                                              <div className="flex items-center gap-1 text-muted-foreground">
                                                                <Calendar className="h-3 w-3" />
                                                                <span className={new Date(task.due_date) < new Date() && !task.completed ? 'text-red-500 font-medium' : ''}>
                                                                  Due: {new Date(task.due_date).toLocaleDateString()}
                                                                </span>
                                                              </div>
                                                            )}
                                                            {task.assigned_by && (
                                                              <div className="flex items-center gap-1 text-muted-foreground">
                                                                <Users className="h-3 w-3" />
                                                                <span>by {task.assigned_by}</span>
                                                              </div>
                                                            )}
                                                          </div>
                                                          {/* Task Updates */}
                                                          {task.updates?.length > 0 && (
                                                            <div className="mt-2 pt-2 border-t border-dashed space-y-1.5">
                                                              {task.updates.map((update, idx) => (
                                                                <div key={update.id || idx} className={`p-2 rounded text-xs ${update.is_admin ? 'bg-blue-500/10' : 'bg-amber-500/10'}`}>
                                                                  <div className="flex items-center justify-between mb-1">
                                                                    <span className={`font-medium ${update.is_admin ? 'text-blue-700' : 'text-amber-700'}`}>
                                                                      {update.user_name} <span className="opacity-70">({update.is_admin ? 'Dev Team' : 'Leadership'})</span>
                                                                    </span>
                                                                    <span className="text-[9px] text-muted-foreground">{new Date(update.created_at).toLocaleString()}</span>
                                                                  </div>
                                                                  <p className="whitespace-pre-wrap">{update.note}</p>
                                                                </div>
                                                              ))}
                                                            </div>
                                                          )}
                                                          {/* Add Update Form */}
                                                          {showUpdateForm?.phaseId === phase.id && showUpdateForm?.deliverableIdx === i && showUpdateForm?.taskId === task.id && (
                                                            <div className="mt-2 pt-2 border-t space-y-2">
                                                              <Textarea placeholder="Add an update note..." value={updateNote} onChange={(e) => setUpdateNote(e.target.value)} rows={2} className="text-xs" autoFocus />
                                                              <div className="flex gap-2">
                                                                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setShowUpdateForm(null); setUpdateNote(''); }}>Cancel</Button>
                                                                <Button size="sm" className="h-6 text-[10px]" onClick={() => addTaskUpdate(phase.id, i, task.id)} disabled={!updateNote.trim()}>
                                                                  <Send className="h-3 w-3 mr-1" /> Post Update
                                                                </Button>
                                                              </div>
                                                            </div>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                })}
                                                {(!item.tasks || item.tasks.length === 0) && (
                                                  <p className="text-xs text-muted-foreground italic text-center py-2">No tasks added yet</p>
                                                )}
                                              </div>
                                              )}
                                              
                                              {/* Add Task Form */}
                                              {(isAdmin || isLeadership) && showTaskForm?.phaseId === phase.id && showTaskForm?.deliverableIdx === i && (
                                                <div className="mt-3 pt-3 border-t space-y-3 bg-muted/30 p-3 rounded-lg">
                                                  <div className="text-xs font-medium text-muted-foreground">Create New Task</div>
                                                  <div>
                                                    <Label className="text-[10px] text-muted-foreground">Task Name *</Label>
                                                    <Input value={taskForm.name} onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Enter task name..." className="h-8 text-xs mt-1" />
                                                  </div>
                                                  <div>
                                                    <Label className="text-[10px] text-muted-foreground">Details (optional)</Label>
                                                    <Textarea value={taskForm.details} onChange={(e) => setTaskForm(prev => ({ ...prev, details: e.target.value }))} placeholder="Add details..." className="text-xs mt-1" rows={2} />
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                      <Label className="text-[10px] text-muted-foreground">Assign To</Label>
                                                      <Select value={taskForm.assignee} onValueChange={(v) => setTaskForm(prev => ({ ...prev, assignee: v }))}>
                                                        <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                                                        <SelectContent>
                                                          {allTeamMembers.map(member => (
                                                            <SelectItem key={member.id} value={member.id} className="text-xs">
                                                              <div className="flex items-center gap-2">
                                                                <Avatar className="h-5 w-5"><AvatarFallback className="text-[9px] bg-indigo-500 text-white">{member.avatar}</AvatarFallback></Avatar>
                                                                <span>{member.name}</span>
                                                              </div>
                                                            </SelectItem>
                                                          ))}
                                                        </SelectContent>
                                                      </Select>
                                                    </div>
                                                    <div>
                                                      <Label className="text-[10px] text-muted-foreground">Due Date</Label>
                                                      <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))} className="h-8 text-xs mt-1" />
                                                    </div>
                                                  </div>
                                                  <div className="flex justify-end gap-2 pt-2">
                                                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setShowTaskForm(null); setTaskForm({ name: '', details: '', assignee: '', due_date: '' }); }}>Cancel</Button>
                                                    <Button size="sm" className="h-7 text-xs" onClick={() => { if (taskForm.name?.trim()) { addTask(phase.id, i, taskForm); setShowTaskForm(null); } }} disabled={!taskForm.name?.trim()}>
                                                      <Plus className="h-3 w-3 mr-1" /> Create Task
                                                    </Button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                          </div>
                        
                          {/* Add deliverable input for this phase */}
                          {!isPhaseCollapsed && isAdmin && (
                            <div className="flex gap-2 mt-2 px-2 pb-2">
                              <Input 
                                value={newDeliverableInputs[phase.id] || ''} 
                                onChange={(e) => setNewDeliverableInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
                                placeholder="Add deliverable to this phase..." 
                                className="h-8 text-xs"
                                onKeyDown={(e) => e.key === 'Enter' && addDeliverable(phase.id)} 
                              />
                              <Button 
                                size="sm" 
                                variant="ghost"
                                className="h-8 w-8 p-0 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                onClick={() => addDeliverable(phase.id)}
                                disabled={!newDeliverableInputs[phase.id]?.trim()}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          
                          {/* Sub-phases Section */}
                          {!isPhaseCollapsed && getSubPhases(phase.id).length > 0 && (
                            <div className="mt-4 space-y-3 border-t border-cyan-500/30 pt-4">
                              <div className="flex items-center gap-2">
                                <Label className="text-xs font-semibold text-cyan-400">Sub-phases</Label>
                                <Badge variant="outline" className="text-[9px] text-cyan-400 border-cyan-400/50">{getSubPhases(phase.id).length}</Badge>
                              </div>
                              {getSubPhases(phase.id).map((subPhase, spIdx) => {
                                const isSubPhaseCollapsed = collapsedSubPhases[subPhase.id] !== false;
                                const subPhaseDeliverables = subPhase.deliverables || [];
                                const subPhaseTasksCompleted = subPhaseDeliverables.reduce((acc, d) => acc + (d.tasks || []).filter(t => t.completed).length, 0);
                                const subPhaseTasksTotal = subPhaseDeliverables.reduce((acc, d) => acc + (d.tasks || []).length, 0);
                                const subPhaseProgress = subPhaseTasksTotal > 0 ? Math.round((subPhaseTasksCompleted / subPhaseTasksTotal) * 100) : 0;
                                
                                return (
                                  <div key={subPhase.id} className="border border-cyan-500/30 rounded-lg bg-cyan-500/5">
                                    {/* Sub-phase Header - Matches parent phase header styling */}
                                    <div 
                                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-cyan-500/10 bg-cyan-500/5"
                                      onClick={() => toggleSubPhaseCollapse(subPhase.id)}
                                    >
                                      <div className="flex items-center gap-2 flex-1 flex-wrap">
                                        {isSubPhaseCollapsed ? <ChevronRight className="h-4 w-4 text-cyan-400" /> : <ChevronDown className="h-4 w-4 text-cyan-400" />}
                                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusOptions.find(s => s.value === subPhase.status)?.color || 'bg-slate-500'}`} />
                                        {isAdmin ? (
                                          <Input 
                                            value={subPhase.name}
                                            onChange={(e) => updateSubPhaseName(phase.id, subPhase.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="h-7 font-semibold text-sm w-[180px] bg-transparent border-cyan-500/30"
                                            placeholder="Sub-phase name..."
                                          />
                                        ) : (
                                          <Label className="cursor-pointer font-semibold text-cyan-300">{subPhase.name}</Label>
                                        )}
                                        {/* Status dropdown/badge - matches parent */}
                                        {isAdmin ? (
                                          <Select 
                                            value={subPhase.status || 'planning'} 
                                            onValueChange={(v) => updateSubPhaseStatus(phase.id, subPhase.id, v)}
                                          >
                                            <SelectTrigger 
                                              className="h-7 text-xs w-[130px]" 
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                              {statusOptions.map(opt => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                  <div className="flex items-center gap-2">
                                                    <div className={`w-2 h-2 rounded-full ${opt.color}`} />
                                                    {opt.label}
                                                  </div>
                                                </SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        ) : (
                                          <Badge variant="outline" className="text-[10px]">
                                            {statusOptions.find(s => s.value === subPhase.status)?.label || 'Planning'}
                                          </Badge>
                                        )}
                                        {/* Due Date - matches parent */}
                                        {isAdmin ? (
                                          <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Calendar className="h-3 w-3 text-muted-foreground" />
                                            <Input 
                                              type="date" 
                                              value={subPhase.due_date || ''} 
                                              onChange={(e) => {
                                                const currentSubPhases = getSubPhases(phase.id);
                                                const updatedSubPhases = currentSubPhases.map(sp => 
                                                  sp.id === subPhase.id ? { ...sp, due_date: e.target.value } : sp
                                                );
                                                setContent({
                                                  ...content,
                                                  sub_phases: {
                                                    ...(content.sub_phases || {}),
                                                    [phase.id]: updatedSubPhases
                                                  }
                                                });
                                              }}
                                              className="h-7 text-xs w-[120px]" 
                                            />
                                          </div>
                                        ) : subPhase.due_date && (
                                          <span className="text-xs text-muted-foreground">Due: {new Date(subPhase.due_date).toLocaleDateString()}</span>
                                        )}
                                        {/* Stats badges - matches parent */}
                                        <Badge variant="secondary" className="text-[10px]">{subPhaseDeliverables.length} deliverables</Badge>
                                        <Badge variant="outline" className={`text-[10px] ${subPhaseTasksCompleted > 0 ? 'border-green-500/50 text-green-600' : ''}`}>
                                          {subPhaseTasksCompleted}/{subPhaseTasksTotal} tasks
                                        </Badge>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-muted-foreground">{subPhaseProgress}%</span>
                                        <Progress value={subPhaseProgress} className="w-16 h-1.5" />
                                        {isAdmin && subPhaseDeliverables.length === 0 && (
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-red-400 hover:text-red-500 hover:bg-red-500/10"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteSubPhase(phase.id, subPhase.id);
                                            }}
                                            title="Delete this sub-phase"
                                          >
                                            <Trash2 className="h-3 w-3" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Sub-phase Content */}
                                    {!isSubPhaseCollapsed && (
                                      <div className="p-2 pt-0 space-y-2">
                                        {subPhaseDeliverables.length === 0 ? (
                                          <p className="text-[10px] text-muted-foreground text-center py-2">No deliverables</p>
                                        ) : (
                                          subPhaseDeliverables.map((deliverable, dIdx) => (
                                            <div key={deliverable.id || dIdx} className="p-2 rounded bg-slate-800/50 border border-slate-700">
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <div className={`w-2 h-2 rounded-full ${statusOptions.find(s => s.value === deliverable.status)?.color || 'bg-slate-500'}`} />
                                                {isAdmin ? (
                                                  <Input 
                                                    value={deliverable.name}
                                                    onChange={(e) => updateSubPhaseDeliverable(phase.id, subPhase.id, dIdx, 'name', e.target.value)}
                                                    className="h-6 text-xs flex-1 bg-transparent min-w-[100px]"
                                                  />
                                                ) : (
                                                  <span className="text-xs flex-1">{deliverable.name}</span>
                                                )}
                                                {/* Due date - inline editable for admin */}
                                                {isAdmin ? (
                                                  <div className="flex items-center gap-1">
                                                    <Calendar className={`h-2.5 w-2.5 ${deliverable.due_date && new Date(deliverable.due_date) < new Date() && deliverable.status !== 'completed' ? 'text-red-400' : 'text-muted-foreground'}`} />
                                                    <Input 
                                                      type="date" 
                                                      value={deliverable.due_date || ''} 
                                                      onChange={(e) => updateSubPhaseDeliverable(phase.id, subPhase.id, dIdx, 'due_date', e.target.value)}
                                                      className={`h-5 text-[10px] w-[100px] bg-transparent ${deliverable.due_date && new Date(deliverable.due_date) < new Date() && deliverable.status !== 'completed' ? 'border-red-500/50 text-red-400' : ''}`}
                                                    />
                                                  </div>
                                                ) : deliverable.due_date ? (
                                                  <Badge variant="outline" className={`text-[9px] ${new Date(deliverable.due_date) < new Date() && deliverable.status !== 'completed' ? 'border-red-500/50 text-red-400' : 'text-muted-foreground'}`}>
                                                    <Calendar className="h-2.5 w-2.5 mr-1" />
                                                    {new Date(deliverable.due_date).toLocaleDateString()}
                                                  </Badge>
                                                ) : null}
                                                {/* Task count badge */}
                                                <Badge variant="outline" className={`text-[9px] ${(deliverable.tasks?.filter(t => t.completed).length || 0) > 0 ? 'border-green-500/50 text-green-400' : 'text-muted-foreground'}`}>
                                                  {deliverable.tasks?.filter(t => t.completed).length || 0}/{deliverable.tasks?.length || 0} tasks
                                                </Badge>
                                                {isAdmin && (
                                                  <>
                                                    <Select 
                                                      value={deliverable.status || 'planning'} 
                                                      onValueChange={(v) => updateSubPhaseDeliverable(phase.id, subPhase.id, dIdx, 'status', v)}
                                                    >
                                                      <SelectTrigger className="h-6 text-[10px] w-[90px]">
                                                        <SelectValue />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        {statusOptions.map(opt => (
                                                          <SelectItem key={opt.value} value={opt.value}>
                                                            <span className="text-[10px]">{opt.label}</span>
                                                          </SelectItem>
                                                        ))}
                                                      </SelectContent>
                                                    </Select>
                                                    <Button
                                                      variant="ghost"
                                                      size="icon"
                                                      className="h-5 w-5 text-red-400"
                                                      onClick={() => deleteSubPhaseDeliverable(phase.id, subPhase.id, dIdx)}
                                                    >
                                                      <Trash2 className="h-3 w-3" />
                                                    </Button>
                                                  </>
                                                )}
                                              </div>
                                              {/* Sub-phase Deliverable Tasks */}
                                              {(deliverable.tasks || []).length > 0 && (
                                                <div className="mt-2 pl-4 space-y-1">
                                                  {deliverable.tasks.map((task, tIdx) => (
                                                    <div key={task.id || tIdx} className="flex items-center gap-2">
                                                      <Checkbox 
                                                        checked={task.completed}
                                                        onCheckedChange={() => toggleSubPhaseTask(phase.id, subPhase.id, dIdx, tIdx)}
                                                        className="h-3 w-3"
                                                      />
                                                      <span className={`text-[10px] ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                                                        {task.name}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                              {/* Add Task to Sub-phase Deliverable */}
                                              {isAdmin && (
                                                <div className="mt-2 flex gap-1">
                                                  <Input 
                                                    placeholder="Add task..."
                                                    className="h-5 text-[10px] flex-1"
                                                    value={newSubPhaseTaskInputs[`${subPhase.id}-${dIdx}`] || ''}
                                                    onChange={(e) => setNewSubPhaseTaskInputs(prev => ({ ...prev, [`${subPhase.id}-${dIdx}`]: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') {
                                                        addSubPhaseTask(phase.id, subPhase.id, dIdx, newSubPhaseTaskInputs[`${subPhase.id}-${dIdx}`] || '');
                                                        setNewSubPhaseTaskInputs(prev => ({ ...prev, [`${subPhase.id}-${dIdx}`]: '' }));
                                                      }
                                                    }}
                                                  />
                                                  <Button 
                                                    size="sm" 
                                                    variant="ghost"
                                                    className="h-5 w-5 p-0 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                                    onClick={() => {
                                                      addSubPhaseTask(phase.id, subPhase.id, dIdx, newSubPhaseTaskInputs[`${subPhase.id}-${dIdx}`] || '');
                                                      setNewSubPhaseTaskInputs(prev => ({ ...prev, [`${subPhase.id}-${dIdx}`]: '' }));
                                                    }}
                                                    disabled={!newSubPhaseTaskInputs[`${subPhase.id}-${dIdx}`]?.trim()}
                                                  >
                                                    <Check className="h-3 w-3" />
                                                  </Button>
                                                </div>
                                              )}
                                            </div>
                                          ))
                                        )}
                                        {/* Add Deliverable to Sub-phase */}
                                        {isAdmin && (
                                          <div className="flex gap-1 mt-2">
                                            <Input 
                                              placeholder="Add deliverable..."
                                              className="h-6 text-xs flex-1"
                                              value={newSubPhaseDeliverableInputs[subPhase.id] || ''}
                                              onChange={(e) => setNewSubPhaseDeliverableInputs(prev => ({ ...prev, [subPhase.id]: e.target.value }))}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                  addSubPhaseDeliverable(phase.id, subPhase.id, newSubPhaseDeliverableInputs[subPhase.id] || '');
                                                  setNewSubPhaseDeliverableInputs(prev => ({ ...prev, [subPhase.id]: '' }));
                                                }
                                              }}
                                            />
                                            <Button 
                                              size="sm" 
                                              variant="ghost"
                                              className="h-6 w-6 p-0 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                              onClick={() => {
                                                addSubPhaseDeliverable(phase.id, subPhase.id, newSubPhaseDeliverableInputs[subPhase.id] || '');
                                                setNewSubPhaseDeliverableInputs(prev => ({ ...prev, [subPhase.id]: '' }));
                                              }}
                                              disabled={!newSubPhaseDeliverableInputs[subPhase.id]?.trim()}
                                            >
                                              <Check className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {/* Droppable placeholder - always visible for drag and drop */}
                        {droppableProvided.placeholder}
                      </div>
                    )}
                  </Droppable>
                );
              })}
              
              {/* Future Features Section - for collaboration before assigning to a phase */}
              <Droppable droppableId="future_features">
                {(ffDropProvided, ffDropSnapshot) => (
                  <div 
                    ref={ffDropProvided.innerRef}
                    {...ffDropProvided.droppableProps}
                    className={`border-2 border-dashed rounded-lg overflow-hidden transition-all duration-200 ${
                      ffDropSnapshot.isDraggingOver 
                        ? 'border-blue-500 bg-blue-500/20 ring-4 ring-blue-500' 
                        : 'border-blue-500/50 bg-blue-500/5'
                    }`}
                  >
                    {/* Future Features Header */}
                    <div 
                      className={`flex items-center justify-between p-3 cursor-pointer transition-all ${
                        ffDropSnapshot.isDraggingOver ? 'bg-blue-500/30' : 'bg-blue-500/10 hover:bg-blue-500/20'
                      }`}
                      onClick={() => togglePhaseCollapse('future_features')}
                    >
                      <div className="flex items-center gap-2">
                        {collapsedPhases.future_features ? <ChevronRight className="h-4 w-4 text-blue-400" /> : <ChevronDown className="h-4 w-4 text-blue-400" />}
                        <Label className="cursor-pointer font-semibold text-white">Future Features</Label>
                        <Badge className="bg-blue-500/20 text-blue-300 text-[10px]">{getFutureFeatureCount()}</Badge>
                        {ffDropSnapshot.isDraggingOver && (
                          <span className="text-xs text-blue-300 font-semibold animate-pulse ml-2">⬇️ Drop here!</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-blue-300 italic">Drag to a phase when ready</span>
                      </div>
                    </div>
                    
                    {/* Future Features Content */}
                    <div 
                      className="p-3 pt-0 transition-all duration-200"
                      style={{ 
                        maxHeight: collapsedPhases.future_features ? '0px' : '2000px',
                        overflow: collapsedPhases.future_features ? 'hidden' : 'visible',
                        opacity: collapsedPhases.future_features ? 0 : 1,
                        padding: collapsedPhases.future_features ? '0 12px' : undefined
                      }}
                    >
                      <div className="space-y-2 mt-3 p-2 rounded-lg">
                        {!collapsedPhases.future_features && (phases.future_features || []).length === 0 && !ffDropSnapshot.isDraggingOver && (
                          <div className="text-center py-6 text-sm text-blue-300/70 italic">
                            <Megaphone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            Add future feature ideas here for team discussion.<br />
                            Drag them to a phase when ready to implement.
                          </div>
                        )}
                        {!collapsedPhases.future_features && (phases.future_features || []).map((item, i) => {
                            const collapseKey = `future_features-${i}`;
                            return (
                              <Draggable key={`future_features-${i}`} draggableId={`future_features-${i}`} index={i} isDragDisabled={!isAdmin && !isLeadership}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                    className={`rounded-lg border-2 border-blue-500/40 bg-slate-800 ${snapshot.isDragging ? 'shadow-2xl ring-4 ring-blue-500 z-[9999] opacity-90' : ''}`}
                                  >
                                    {/* Feature Header */}
                                    <div className="flex items-center gap-2 p-3 cursor-pointer select-none" onClick={() => !snapshot.isDragging && toggleCollapse('future_features', i)}>
                                      {(isAdmin || isLeadership) && (
                                        <div 
                                          {...provided.dragHandleProps} 
                                          className="cursor-grab active:cursor-grabbing hover:bg-blue-500/20 rounded p-1"
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) => e.stopPropagation()}
                                        >
                                          <GripVertical className="h-4 w-4 text-blue-400" />
                                        </div>
                                      )}
                                      <Megaphone className="h-4 w-4 text-blue-400 flex-shrink-0" />
                                      <span className="font-semibold text-sm flex-1 truncate text-white">{item.name || 'Untitled Feature'}</span>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {item.tasks?.length > 0 && (
                                          <Badge variant="outline" className="text-[10px] border-blue-500/30 text-blue-300">
                                            {item.tasks?.filter(t => t.completed).length || 0}/{item.tasks?.length || 0} tasks
                                          </Badge>
                                        )}
                                        {collapsedDeliverables[collapseKey] === false ? <ChevronDown className="h-4 w-4 text-blue-400" /> : <ChevronRight className="h-4 w-4 text-blue-400" />}
                                      </div>
                                    </div>
                                    
                                    {/* Feature Collapsible Content */}
                                    {collapsedDeliverables[collapseKey] === false && (
                                      <div className="px-3 pb-3 pt-0 space-y-2 border-t border-blue-500/20">
                                        <div className="flex items-center justify-between gap-2 pt-2">
                                          {(isAdmin || isLeadership) ? (
                                            <Input 
                                              value={item.name} 
                                              onChange={(e) => updateDeliverable('future_features', i, 'name', e.target.value)}
                                              className="h-8 font-semibold flex-1 border-blue-500/30 focus:border-blue-500 text-white"
                                              placeholder="Feature name..."
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          ) : (
                                            <span className="font-semibold text-sm text-white">{item.name}</span>
                                          )}
                                          <div className="flex items-center gap-1 flex-shrink-0">
                                            {/* Move to Phase dropdown */}
                                            {(isAdmin || isLeadership) && (
                                              <Select 
                                                value="" 
                                                onValueChange={(targetPhaseId) => {
                                                  moveDeliverable('future_features', i, targetPhaseId);
                                                }}
                                              >
                                                <SelectTrigger 
                                                  className="h-7 w-[130px] text-xs border-blue-500/30"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <ArrowRightLeft className="h-3 w-3 mr-1" />
                                                  <span>Move to...</span>
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {PHASES.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                  ))}
                                                  <SelectItem value="exclusions">Exclusions</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            )}
                                            {(isAdmin || isLeadership) && (
                                              <Button variant="ghost" size="icon" className="h-6 w-6 hover:bg-blue-500/20" onClick={(e) => { e.stopPropagation(); removeDeliverable('future_features', i); }}>
                                                <X className="h-3 w-3" />
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {/* Description/Notes for the feature idea */}
                                        <div>
                                          <Label className="text-[10px] text-blue-300">Notes / Description</Label>
                                          <Textarea 
                                            value={item.description || ''} 
                                            onChange={(e) => updateDeliverable('future_features', i, 'description', e.target.value)}
                                            placeholder="Describe this feature idea, discuss requirements, etc..."
                                            className="text-xs mt-1 border-blue-500/30 focus:border-blue-500 text-white"
                                            rows={2}
                                            disabled={!isAdmin && !isLeadership}
                                          />
                                        </div>
                                        
                                        {/* Tasks section for future features */}
                                        <div className="mt-2 pt-2 border-t border-blue-500/20">
                                          <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs text-blue-300 flex items-center gap-2">
                                              Discussion / Tasks
                                              <Badge variant="secondary" className="text-[10px] bg-blue-500/10">
                                                {item.tasks?.filter(t => t.completed).length || 0}/{item.tasks?.length || 0}
                                              </Badge>
                                            </Label>
                                            {(isAdmin || isLeadership) && (
                                              <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="h-6 text-[10px] px-2 border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setShowTaskForm(showTaskForm?.phaseId === 'future_features' && showTaskForm?.deliverableIdx === i ? null : { phaseId: 'future_features', deliverableIdx: i });
                                                  setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                                  fetchPlatformUsers();
                                                }}
                                              >
                                                <Plus className="h-3 w-3 mr-1" /> Add Note
                                              </Button>
                                            )}
                                          </div>
                                          
                                          {/* Task/Note list */}
                                          <div className="space-y-2">
                                            {item.tasks?.map((task) => {
                                              const assigneeInfo = allTeamMembers.find(m => m.id === task.assignee || m.name === task.assignee);
                                              return (
                                                <div key={task.id} className={`p-2 rounded-lg border ${task.completed ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-blue-500/20'}`}>
                                                  <div className="flex items-start gap-2">
                                                    <div className="flex-1 space-y-1">
                                                      <span className={`text-sm text-white ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.name}</span>
                                                      {task.details && <p className="text-xs text-slate-300">{task.details}</p>}
                                                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                                        {task.assigned_by && <span>by {task.assigned_by}</span>}
                                                        {task.assigned_at && <span>• {new Date(task.assigned_at).toLocaleDateString()}</span>}
                                                      </div>
                                                    </div>
                                                    {(isAdmin || isLeadership) && (
                                                      <div className="flex items-center gap-1">
                                                        <Button size="sm" variant={task.completed ? "secondary" : "outline"} className={`h-6 text-[10px] px-2 ${task.completed ? 'bg-green-500/20' : ''}`} onClick={() => toggleTask('future_features', i, task.id)}>
                                                          {task.completed ? '✓' : 'Done'}
                                                        </Button>
                                                        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeTask('future_features', i, task.id)}>
                                                          <X className="h-3 w-3" />
                                                        </Button>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              );
                                            })}
                                          </div>
                                          
                                          {/* Add Note Form */}
                                          {(isAdmin || isLeadership) && showTaskForm?.phaseId === 'future_features' && showTaskForm?.deliverableIdx === i && (
                                            <div className="mt-2 pt-2 border-t border-blue-500/20 space-y-2 bg-blue-500/5 p-2 rounded">
                                              <Input value={taskForm.name} onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Add a note or task..." className="h-8 text-xs" />
                                              <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowTaskForm(null)}>Cancel</Button>
                                                <Button size="sm" className="h-6 text-xs bg-blue-500 hover:bg-blue-600" onClick={() => { if (taskForm.name?.trim()) { addTask('future_features', i, taskForm); setShowTaskForm(null); } }} disabled={!taskForm.name?.trim()}>
                                                  <Plus className="h-3 w-3 mr-1" /> Add
                                                </Button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                      </div>
                    
                      {/* Add future feature input */}
                      {!collapsedPhases.future_features && (isAdmin || isLeadership) && (
                        <div className="flex gap-2 mt-2 px-2 pb-2">
                          <Input 
                            value={newDeliverableInputs.future_features || ''} 
                            onChange={(e) => setNewDeliverableInputs(prev => ({ ...prev, future_features: e.target.value }))}
                            placeholder="Add a future feature idea..." 
                            className="h-8 text-xs border-blue-500/30 focus:border-blue-500"
                            onKeyDown={(e) => e.key === 'Enter' && addDeliverable('future_features')} 
                          />
                          <Button size="sm" className="h-8 bg-blue-500 hover:bg-blue-600" onClick={() => addDeliverable('future_features')}><Plus className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </div>
                    {/* Droppable placeholder - always visible for drag and drop */}
                    {ffDropProvided.placeholder}
                  </div>
                )}
              </Droppable>
              
              {/* Exclusions Section - Interactive like Future Features with drag-and-drop */}
              <Droppable droppableId="exclusions">
                {(exDropProvided, exDropSnapshot) => (
                  <div 
                    ref={exDropProvided.innerRef}
                    {...exDropProvided.droppableProps}
                    className={`border-2 border-dashed rounded-lg overflow-hidden transition-all duration-200 ${
                      exDropSnapshot.isDraggingOver 
                        ? 'border-red-500 bg-red-500/20 ring-4 ring-red-500' 
                        : 'border-red-500/50 bg-red-500/5'
                    }`}
                  >
                    {/* Exclusions Header */}
                    <div 
                      className={`flex items-center justify-between p-3 cursor-pointer transition-all ${
                        exDropSnapshot.isDraggingOver ? 'bg-red-500/30' : 'bg-red-500/10 hover:bg-red-500/20'
                      }`}
                      onClick={() => setCollapsedPhases(prev => ({ ...prev, exclusions: !prev.exclusions }))}
                    >
                      <div className="flex items-center gap-2">
                        {collapsedPhases.exclusions ? <ChevronRight className="h-4 w-4 text-red-400" /> : <ChevronDown className="h-4 w-4 text-red-400" />}
                        <XCircle className="h-4 w-4 text-red-400" />
                        <Label className="cursor-pointer font-semibold text-white">Exclusions (Out of Scope)</Label>
                        <Badge className="bg-red-500/20 text-red-300 text-[10px]">{getExclusionsCount()}</Badge>
                        {exDropSnapshot.isDraggingOver && (
                          <span className="text-xs text-red-300 font-semibold animate-pulse ml-2">⬇️ Drop here!</span>
                        )}
                      </div>
                      <span className="text-xs text-red-300 italic">Drag to a phase when ready</span>
                    </div>
                    
                    {/* Exclusions Content */}
                    <div 
                      className="p-3 pt-0 transition-all duration-200"
                      style={{ 
                        maxHeight: collapsedPhases.exclusions ? '0px' : '2000px',
                        overflow: collapsedPhases.exclusions ? 'hidden' : 'visible',
                        opacity: collapsedPhases.exclusions ? 0 : 1,
                        padding: collapsedPhases.exclusions ? '0 12px' : undefined
                      }}
                    >
                      <div className="space-y-2 mt-3 p-2 rounded-lg">
                        {!collapsedPhases.exclusions && getExclusionItems().length === 0 && !exDropSnapshot.isDraggingOver && (
                          <div className="text-center py-6 text-sm text-red-300/70 italic">
                            <XCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                            Define items that are explicitly out of scope.<br />
                            Drag them to a phase when ready to implement.
                          </div>
                        )}
                        {!collapsedPhases.exclusions && getExclusionItems().map((item, i) => {
                            const collapseKey = `exclusions-${i}`;
                            const isCollapsed = collapsedDeliverables[collapseKey] !== false; // Default to collapsed
                            return (
                              <Draggable key={`exclusions-${i}`} draggableId={`exclusions-${i}`} index={i} isDragDisabled={!isAdmin && !isLeadership}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    style={provided.draggableProps.style}
                                    className={`rounded-lg border-2 border-red-500/40 bg-slate-800 ${snapshot.isDragging ? 'shadow-2xl ring-4 ring-red-500 z-[9999] opacity-90' : ''}`}
                                  >
                                    {/* Exclusion Header */}
                                    <div 
                                      className="flex items-center gap-2 p-3 cursor-pointer select-none" 
                                      onClick={() => !snapshot.isDragging && setCollapsedDeliverables(prev => ({ ...prev, [collapseKey]: prev[collapseKey] === false ? true : false }))}
                                    >
                                      {(isAdmin || isLeadership) && (
                                        <div 
                                          {...provided.dragHandleProps} 
                                          className="cursor-grab active:cursor-grabbing hover:bg-red-500/20 rounded p-1"
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) => e.stopPropagation()}
                                        >
                                          <GripVertical className="h-4 w-4 text-red-400" />
                                        </div>
                                      )}
                                      <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                                      <span className="font-semibold text-sm flex-1 truncate text-white">
                                        {item.name || 'Untitled Exclusion'}
                                      </span>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        {item.notes?.length > 0 && (
                                          <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-300">
                                            {item.notes.length} note{item.notes.length > 1 ? 's' : ''}
                                          </Badge>
                                        )}
                                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-red-400" /> : <ChevronDown className="h-4 w-4 text-red-400" />}
                                      </div>
                                    </div>
                                    
                                    {/* Exclusion Collapsible Content */}
                                    {!isCollapsed && (
                                      <div className="px-3 pb-3 pt-0 space-y-2 border-t border-red-500/20">
                                        <div className="flex items-center justify-between gap-2 pt-2">
                                          {(isAdmin || isLeadership) ? (
                                            <Input 
                                              value={item.name} 
                                              onChange={(e) => updateExclusionItem(i, 'name', e.target.value)}
                                              className="h-8 font-semibold flex-1 border-red-500/30 focus:border-red-500 text-white"
                                              placeholder="Exclusion name..."
                                              onClick={(e) => e.stopPropagation()}
                                            />
                                          ) : (
                                            <span className="font-semibold text-sm text-white">{item.name}</span>
                                          )}
                                          <div className="flex items-center gap-1 flex-shrink-0">
                                            {/* Move to Phase dropdown */}
                                            {(isAdmin || isLeadership) && (
                                              <Select 
                                                value="" 
                                                onValueChange={(targetPhaseId) => {
                                                  moveExclusionToPhase(i, targetPhaseId);
                                                }}
                                              >
                                                <SelectTrigger 
                                                  className="h-7 w-[130px] text-xs border-red-500/30"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <ArrowRightLeft className="h-3 w-3 mr-1" />
                                                  <span>Move to...</span>
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {PHASES.map(p => (
                                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                                  ))}
                                                  <SelectItem value="future_features">Future Features</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            )}
                                            {(isAdmin || isLeadership) && (
                                              <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6 hover:bg-red-500/20" 
                                                onClick={(e) => { e.stopPropagation(); removeExclusionItem(i); }}
                                              >
                                                <X className="h-3 w-3" />
                                              </Button>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {/* Description/Reason for exclusion */}
                                        <div>
                                          <Label className="text-[10px] text-red-300">Reason / Description</Label>
                                          <Textarea 
                                            value={item.description || ''} 
                                            onChange={(e) => updateExclusionItem(i, 'description', e.target.value)}
                                            placeholder="Why is this excluded? Any relevant notes..."
                                            className="text-xs mt-1 border-red-500/30 focus:border-red-500 text-white"
                                            rows={2}
                                            disabled={!isAdmin && !isLeadership}
                                          />
                                        </div>
                                        
                                        {/* Notes section for exclusions */}
                                        <div className="mt-2 pt-2 border-t border-red-500/20">
                                          <div className="flex items-center justify-between mb-2">
                                            <Label className="text-xs text-red-300 flex items-center gap-2">
                                              Notes / Discussion
                                              <Badge variant="secondary" className="text-[10px] bg-red-500/10">
                                                {item.notes?.length || 0}
                                              </Badge>
                                            </Label>
                                            {(isAdmin || isLeadership) && (
                                              <Button 
                                                size="sm" 
                                                variant="outline" 
                                                className="h-6 text-[10px] px-2 border-red-500/30 text-red-300 hover:bg-red-500/10"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setShowTaskForm(showTaskForm?.phaseId === 'exclusions' && showTaskForm?.deliverableIdx === i ? null : { phaseId: 'exclusions', deliverableIdx: i });
                                                  setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                                }}
                                              >
                                                <Plus className="h-3 w-3 mr-1" /> Add Note
                                              </Button>
                                            )}
                                          </div>
                                          
                                          {/* Notes list */}
                                          <div className="space-y-2">
                                            {item.notes?.map((note, noteIdx) => (
                                              <div key={note.id || noteIdx} className="p-2 rounded-lg border bg-slate-700/50 border-red-500/20">
                                                <div className="flex items-start gap-2">
                                                  <div className="flex-1 space-y-1">
                                                    <span className="text-sm text-white">{note.name || note.text}</span>
                                                    {note.details && <p className="text-xs text-slate-300">{note.details}</p>}
                                                    <div className="flex items-center gap-2 text-[10px] text-slate-400">
                                                      {note.assigned_by && <span>by {note.assigned_by}</span>}
                                                      {note.assigned_at && <span>• {new Date(note.assigned_at).toLocaleDateString()}</span>}
                                                    </div>
                                                  </div>
                                                  {(isAdmin || isLeadership) && (
                                                    <Button 
                                                      variant="ghost" 
                                                      size="icon" 
                                                      className="h-6 w-6" 
                                                      onClick={() => removeExclusionNote(i, noteIdx)}
                                                    >
                                                      <X className="h-3 w-3" />
                                                    </Button>
                                                  )}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                          
                                          {/* Add Note Form */}
                                          {(isAdmin || isLeadership) && showTaskForm?.phaseId === 'exclusions' && showTaskForm?.deliverableIdx === i && (
                                            <div className="mt-2 pt-2 border-t border-red-500/20 space-y-2 bg-red-500/5 p-2 rounded">
                                              <Input 
                                                value={taskForm.name} 
                                                onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} 
                                                placeholder="Add a note..." 
                                                className="h-8 text-xs" 
                                              />
                                              <div className="flex justify-end gap-2">
                                                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={() => setShowTaskForm(null)}>Cancel</Button>
                                                <Button 
                                                  size="sm" 
                                                  className="h-6 text-xs bg-red-500 hover:bg-red-600" 
                                                  onClick={() => { 
                                                    if (taskForm.name?.trim()) { 
                                                      addExclusionNote(i, taskForm); 
                                                      setShowTaskForm(null); 
                                                    } 
                                                  }} 
                                                  disabled={!taskForm.name?.trim()}
                                                >
                                                  <Plus className="h-3 w-3 mr-1" /> Add
                                                </Button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                      </div>
                    
                      {/* Add exclusion input */}
                      {!collapsedPhases.exclusions && (isAdmin || isLeadership) && (
                        <div className="flex gap-2 mt-2 px-2 pb-2">
                          <Input 
                            value={newExclusion} 
                            onChange={(e) => setNewExclusion(e.target.value)} 
                            placeholder="Add an exclusion item..." 
                            className="h-8 text-xs border-red-500/30 focus:border-red-500"
                            onKeyDown={(e) => e.key === 'Enter' && addExclusionItem()} 
                          />
                          <Button size="sm" className="h-8 bg-red-500 hover:bg-red-600" onClick={addExclusionItem}>
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                    {/* Droppable placeholder - always visible for drag and drop */}
                    {exDropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </DragDropContext>
          <div>
            <Label>Success Criteria</Label>
            <Textarea value={content.success_criteria || ''} 
              onChange={(e) => setContent({...content, success_criteria: e.target.value})}
              rows={2} disabled={!isAdmin} className="mt-1" />
          </div>
          <GovernanceFeedbackSection 
            pillar={pillar} 
            isAdmin={isAdmin} 
            isLeadership={isLeadership} 
            currentUser={currentUser} 
            onSave={onSave} 
          />
        </div>
        {(isAdmin || isLeadership) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
const DataSecurityDialog = ({ pillar, open, onOpenChange, onSave, isAdmin, isLeadership, currentUser, onRefresh }) => {
  const [content, setContent] = useState(pillar?.content || {});
  const [saving, setSaving] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});
  const [collapsedItems, setCollapsedItems] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', details: '', assignee: '', due_date: '' });
  const [platformUsers, setPlatformUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [allCollapsed, setAllCollapsed] = useState(true);
  const [newItemInputs, setNewItemInputs] = useState({});

  useEffect(() => {
    if (pillar) setContent(pillar.content || {});
  }, [pillar]);

  // Default team members
  const defaultTeamMembers = [
    { id: 'ryan_march', name: 'Ryan March', role: 'Lead Developer', avatar: 'RM' },
    { id: 'ryan_adams', name: 'Ryan Adams', role: 'Developer', avatar: 'RA' },
    { id: 'gabe', name: 'Gabriel Gecan', role: 'Developer', avatar: 'GG' },
  ];

  // Fetch platform users
  const fetchPlatformUsers = async () => {
    if (platformUsers.length > 0) return;
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = response.data.map(u => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        role: u.role?.replace(/_/g, ' '),
        avatar: `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()
      }));
      setPlatformUsers(users);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const allTeamMembers = [...defaultTeamMembers, ...platformUsers.filter(pu => 
    !defaultTeamMembers.find(dm => dm.name.toLowerCase() === pu.name.toLowerCase())
  )];

  // Security phases
  const SEC_PHASES = [
    { id: 'compliance', name: 'Compliance', color: 'red', icon: Shield },
    { id: 'data_handling', name: 'Data Handling', color: 'blue', icon: Shield },
    { id: 'security_measures', name: 'Security Measures', color: 'emerald', icon: ShieldCheck },
    { id: 'monitoring', name: 'Monitoring & Auditing', color: 'amber', icon: Shield },
  ];

  // Normalize phases structure
  const normalizePhases = (content) => {
    if (content.phases && typeof content.phases === 'object') {
      return content.phases;
    }
    // Migration: Convert old format to new phase-based format
    const phases = {};
    SEC_PHASES.forEach(p => { phases[p.id] = []; });
    
    // Migrate compliance items
    if (content.compliance?.length > 0) {
      phases.compliance = content.compliance.map((c, i) => ({
        id: Date.now() + i,
        name: typeof c === 'string' ? c : c.name,
        status: 'in_progress',
        tasks: []
      }));
    }
    
    // Migrate security measures
    if (content.security_measures?.length > 0) {
      phases.security_measures = content.security_measures.map((s, i) => ({
        id: Date.now() + 100 + i,
        name: typeof s === 'string' ? s : s.name,
        status: 'completed',
        tasks: []
      }));
    }
    
    return phases;
  };

  const phases = normalizePhases(content);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(pillar.id, { content: { ...content, phases } });
      toast.success('Data & Security updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Item management
  const addItem = (phaseId) => {
    const inputValue = newItemInputs[phaseId];
    if (!inputValue?.trim()) return;
    const newItem = { 
      id: Date.now(),
      name: inputValue.trim(), 
      status: 'pending', 
      due_date: '', 
      progress: 0,
      tasks: []
    };
    const updatedPhases = { ...phases, [phaseId]: [...(phases[phaseId] || []), newItem] };
    setContent({ ...content, phases: updatedPhases });
    setNewItemInputs(prev => ({ ...prev, [phaseId]: '' }));
  };

  const updateItem = (phaseId, idx, field, value) => {
    const updated = [...(phases[phaseId] || [])];
    updated[idx] = { ...updated[idx], [field]: value };
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  const removeItem = (phaseId, idx) => {
    const updated = (phases[phaseId] || []).filter((_, i) => i !== idx);
    setContent({ ...content, phases: { ...phases, [phaseId]: updated } });
  };

  // Task management
  const addTask = (phaseId, itemIdx, taskData) => {
    if (!taskData.name?.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const newTask = { 
      id: Date.now(), 
      name: taskData.name.trim(), 
      details: taskData.details || '',
      assignee: taskData.assignee || '',
      due_date: taskData.due_date || '',
      completed: false,
      assigned_by: userName,
      assigned_at: new Date().toISOString(),
    };
    const phaseItems = [...(phases[phaseId] || [])];
    const tasks = [...(phaseItems[itemIdx].tasks || []), newTask];
    phaseItems[itemIdx] = { ...phaseItems[itemIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[itemIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
    setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
    setShowTaskForm(null);
  };

  const toggleTask = (phaseId, itemIdx, taskId) => {
    const phaseItems = [...(phases[phaseId] || [])];
    const completedBy = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const tasks = phaseItems[itemIdx].tasks.map(t => 
      t.id === taskId ? { 
        ...t, 
        completed: !t.completed,
        completed_by: !t.completed ? completedBy : null,
        completed_at: !t.completed ? new Date().toISOString() : null
      } : t
    );
    phaseItems[itemIdx] = { ...phaseItems[itemIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[itemIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    if (phaseItems[itemIdx].progress === 100) {
      phaseItems[itemIdx].status = 'completed';
    } else if (phaseItems[itemIdx].progress > 0) {
      phaseItems[itemIdx].status = 'in_progress';
    }
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
  };

  const removeTask = (phaseId, itemIdx, taskId) => {
    const phaseItems = [...(phases[phaseId] || [])];
    const tasks = phaseItems[itemIdx].tasks.filter(t => t.id !== taskId);
    phaseItems[itemIdx] = { ...phaseItems[itemIdx], tasks };
    const completedCount = tasks.filter(t => t.completed).length;
    phaseItems[itemIdx].progress = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;
    setContent({ ...content, phases: { ...phases, [phaseId]: phaseItems } });
  };

  // Calculate progress
  const calculateOverallProgress = () => {
    let totalTasks = 0;
    let completedTasks = 0;
    SEC_PHASES.forEach(phase => {
      (phases[phase.id] || []).forEach(item => {
        if (item.tasks && item.tasks.length > 0) {
          totalTasks += item.tasks.length;
          completedTasks += item.tasks.filter(t => t.completed).length;
        }
      });
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  const calculatePhaseProgress = (phaseId) => {
    let totalTasks = 0;
    let completedTasks = 0;
    (phases[phaseId] || []).forEach(item => {
      if (item.tasks && item.tasks.length > 0) {
        totalTasks += item.tasks.length;
        completedTasks += item.tasks.filter(t => t.completed).length;
      }
    });
    return totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  };

  // Calculate item progress from tasks (source of truth)
  const getItemProgress = (item) => {
    const tasks = item?.tasks || [];
    if (tasks.length === 0) return 0;
    const completedTasks = tasks.filter(t => t.completed).length;
    return Math.round((completedTasks / tasks.length) * 100);
  };

  const overallProgress = calculateOverallProgress();

  const togglePhaseCollapse = (phaseId) => {
    setCollapsedPhases(prev => ({ ...prev, [phaseId]: !prev[phaseId] }));
  };

  const toggleItemCollapse = (phaseId, idx) => {
    const key = `${phaseId}-${idx}`;
    setCollapsedItems(prev => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const toggleAllCollapse = () => {
    if (allCollapsed) {
      setCollapsedPhases({});
      const allItemsExpanded = {};
      SEC_PHASES.forEach(phase => {
        (phases[phase.id] || []).forEach((_, idx) => { 
          allItemsExpanded[`${phase.id}-${idx}`] = false; 
        });
      });
      setCollapsedItems(allItemsExpanded);
    } else {
      const allPhasesCollapsed = {};
      SEC_PHASES.forEach(phase => { allPhasesCollapsed[phase.id] = true; });
      setCollapsedPhases(allPhasesCollapsed);
      setCollapsedItems({});
    }
    setAllCollapsed(!allCollapsed);
  };

  const statusOptions = [
    { value: 'planning', label: 'Planning', color: 'bg-slate-500' },
    { value: 'design', label: 'Design', color: 'bg-purple-500' },
    { value: 'building', label: 'Building', color: 'bg-blue-400' },
    { value: 'coding', label: 'Coding', color: 'bg-blue-600' },
    { value: 'internal_testing', label: 'Internal Testing', color: 'bg-cyan-500' },
    { value: 'team_testing', label: 'Team Testing', color: 'bg-teal-500' },
    { value: 'debug', label: 'De-Bug', color: 'bg-orange-500' },
    { value: 'edit_upgrades', label: 'Edit/Upgrades', color: 'bg-amber-500' },
    { value: 'final_testing', label: 'Final Testing', color: 'bg-indigo-500' },
    { value: 'release_ready', label: 'Release Ready', color: 'bg-emerald-500' },
    { value: 'needs_attention', label: 'Needs Attention', color: 'bg-red-500' },
    { value: 'completed', label: 'Completed', color: 'bg-green-500' },
  ];

  const getPhaseColor = (phaseId) => {
    const phase = SEC_PHASES.find(p => p.id === phaseId);
    const colorMap = {
      red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' },
      blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400' },
      emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
      amber: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' },
    };
    return colorMap[phase?.color] || colorMap.red;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-red-500" /> Data & Security
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Compliance, data handling, and security measures</span>
            <div className="flex items-center gap-2">
              <Progress value={overallProgress} className="w-24 h-2" />
              <span className="text-xs font-medium text-red-600">{overallProgress}%</span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Editable Data Handling Info */}
          <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
            <Label className="text-blue-400">Data Handling Overview</Label>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-[10px] text-muted-foreground">Storage</Label>
                {isAdmin ? (
                  <Input 
                    value={content.data_handling?.storage || ''} 
                    onChange={(e) => setContent({
                      ...content, 
                      data_handling: { ...content.data_handling, storage: e.target.value }
                    })}
                    className="h-8 text-xs mt-1"
                    placeholder="e.g., MongoDB Atlas (AWS us-east-1)"
                  />
                ) : (
                  <p className="text-sm">{content.data_handling?.storage || 'Not specified'}</p>
                )}
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Backup</Label>
                {isAdmin ? (
                  <Input 
                    value={content.data_handling?.backup || ''} 
                    onChange={(e) => setContent({
                      ...content, 
                      data_handling: { ...content.data_handling, backup: e.target.value }
                    })}
                    className="h-8 text-xs mt-1"
                    placeholder="e.g., Daily automated backups, 30-day retention"
                  />
                ) : (
                  <p className="text-sm">{content.data_handling?.backup || 'Not specified'}</p>
                )}
              </div>
              <div>
                <Label className="text-[10px] text-muted-foreground">Access Control</Label>
                {isAdmin ? (
                  <Input 
                    value={content.data_handling?.access_control || ''} 
                    onChange={(e) => setContent({
                      ...content, 
                      data_handling: { ...content.data_handling, access_control: e.target.value }
                    })}
                    className="h-8 text-xs mt-1"
                    placeholder="e.g., Role-based access control (RBAC)"
                  />
                ) : (
                  <p className="text-sm">{content.data_handling?.access_control || 'Not specified'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Editable Data Retention */}
          <div>
            <Label>Data Retention Policy</Label>
            {isAdmin ? (
              <Textarea 
                value={content.data_retention || ''} 
                onChange={(e) => setContent({...content, data_retention: e.target.value})}
                className="mt-1 text-sm"
                rows={2}
                placeholder="e.g., Customer data retained for 7 years per regulatory requirements..."
              />
            ) : (
              <p className="text-sm mt-1 text-muted-foreground">{content.data_retention || 'Not specified'}</p>
            )}
          </div>

          {/* Phase Control */}
          <div className="flex items-center justify-between">
            <Label className="text-base font-semibold">Security & Compliance Areas</Label>
            <Button size="sm" variant="outline" onClick={toggleAllCollapse}>
              {allCollapsed ? <Maximize2 className="h-3 w-3 mr-1" /> : <Minimize2 className="h-3 w-3 mr-1" />}
              {allCollapsed ? 'Expand All' : 'Collapse All'}
            </Button>
          </div>

          {/* Phases */}
          <div className="space-y-3">
            {SEC_PHASES.map((phase) => {
              const PhaseIcon = phase.icon;
              const phaseColors = getPhaseColor(phase.id);
              const isPhaseCollapsed = collapsedPhases[phase.id];
              const phaseProgress = calculatePhaseProgress(phase.id);
              const itemCount = (phases[phase.id] || []).length;
              const completedItems = (phases[phase.id] || []).filter(item => item.status === 'completed').length;

              return (
                <div key={phase.id} className={`rounded-lg border-2 ${phaseColors.border} ${phaseColors.bg}`}>
                  {/* Phase Header */}
                  <div 
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                    onClick={() => togglePhaseCollapse(phase.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isPhaseCollapsed ? <ChevronRight className={`h-4 w-4 ${phaseColors.text}`} /> : <ChevronDown className={`h-4 w-4 ${phaseColors.text}`} />}
                      <PhaseIcon className={`h-4 w-4 ${phaseColors.text}`} />
                      <span className="font-semibold">{phase.name}</span>
                      <Badge variant="outline" className={`text-[10px] ${phaseColors.border}`}>
                        {completedItems}/{itemCount} items
                      </Badge>
                      {phaseProgress > 0 && (
                        <Badge className={`text-[10px] ${phaseProgress === 100 ? 'bg-green-500' : 'bg-amber-500'}`}>
                          {phaseProgress}%
                        </Badge>
                      )}
                    </div>
                    <Progress value={phaseProgress} className="w-24 h-1.5" />
                  </div>

                  {/* Phase Content */}
                  {!isPhaseCollapsed && (
                    <div className="px-3 pb-3 space-y-2">
                      {(phases[phase.id] || []).map((item, i) => {
                        const itemKey = `${phase.id}-${i}`;
                        const isItemCollapsed = collapsedItems[itemKey] !== false;
                        const taskCount = item.tasks?.length || 0;
                        const completedTasks = item.tasks?.filter(t => t.completed).length || 0;

                        return (
                          <div key={item.id || i} className="rounded-lg border bg-slate-800/50">
                            {/* Item Header */}
                            <div 
                              className="flex items-center gap-2 p-3 cursor-pointer"
                              onClick={() => toggleItemCollapse(phase.id, i)}
                            >
                              <div className={`w-2.5 h-2.5 rounded-full ${statusOptions.find(s => s.value === item.status)?.color || 'bg-slate-400'}`} />
                              <ShieldCheck className="h-4 w-4 text-green-500" />
                              <span className="font-medium text-sm flex-1">{item.name}</span>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {taskCount > 0 && (
                                  <Badge variant="outline" className="text-[10px]">
                                    {completedTasks}/{taskCount} tasks
                                  </Badge>
                                )}
                                {isItemCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </div>
                            </div>

                            {/* Item Content */}
                            {!isItemCollapsed && (
                              <div className="px-3 pb-3 pt-0 space-y-3 border-t border-slate-700">
                                {/* Item Fields */}
                                {isAdmin && (
                                  <div className="grid grid-cols-3 gap-2 pt-2">
                                    <div className="col-span-1">
                                      <Label className="text-[10px] text-muted-foreground">Name</Label>
                                      <Input 
                                        value={item.name} 
                                        onChange={(e) => updateItem(phase.id, i, 'name', e.target.value)} 
                                        className="h-8 text-xs"
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-[10px] text-muted-foreground">Status</Label>
                                      <Select value={item.status || 'pending'} onValueChange={(v) => updateItem(phase.id, i, 'status', v)}>
                                        <SelectTrigger className="h-8 text-xs">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {statusOptions.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                    <div className="flex items-end gap-1">
                                      <div className="flex-1">
                                        <Label className="text-[10px] text-muted-foreground">Due Date</Label>
                                        <Input type="date" value={item.due_date || ''} onChange={(e) => updateItem(phase.id, i, 'due_date', e.target.value)} className="h-8 text-xs" />
                                      </div>
                                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem(phase.id, i)}>
                                        <Trash2 className="h-3 w-3 text-red-400" />
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                {/* Progress Bar */}
                                <div className="flex items-center gap-2">
                                  <Progress value={getItemProgress(item)} className="flex-1 h-1.5" />
                                  <span className="text-xs text-muted-foreground">{getItemProgress(item)}%</span>
                                </div>

                                {/* Tasks Section */}
                                <div className="pt-2 border-t border-slate-700">
                                  <div className="flex items-center justify-between mb-2">
                                    <Label className="text-xs flex items-center gap-2">
                                      Tasks
                                      <Badge variant="secondary" className="text-[10px]">{completedTasks}/{taskCount}</Badge>
                                    </Label>
                                    {(isAdmin || isLeadership) && (
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="h-6 text-[10px] px-2"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowTaskForm(showTaskForm?.phaseId === phase.id && showTaskForm?.itemIdx === i ? null : { phaseId: phase.id, itemIdx: i });
                                          setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                          fetchPlatformUsers();
                                        }}
                                      >
                                        <Plus className="h-3 w-3 mr-1" /> Add Task
                                      </Button>
                                    )}
                                  </div>

                                  {/* Task List */}
                                  <div className="space-y-1">
                                    {item.tasks?.map((task) => {
                                      const isTaskExpanded = expandedTasks[task.id];
                                      return (
                                        <div key={task.id} className={`p-2 rounded border ${task.completed ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
                                          <div 
                                            className="flex items-center gap-2 cursor-pointer"
                                            onClick={() => setExpandedTasks(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                                          >
                                            <Checkbox 
                                              checked={task.completed}
                                              onCheckedChange={() => toggleTask(phase.id, i, task.id)}
                                              onClick={(e) => e.stopPropagation()}
                                              disabled={!isAdmin && !isLeadership}
                                            />
                                            <span className={`flex-1 text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.name}</span>
                                            {task.assignee && (
                                              <Badge variant="outline" className="text-[10px]">
                                                {allTeamMembers.find(m => m.id === task.assignee)?.name || task.assignee}
                                              </Badge>
                                            )}
                                            {isTaskExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                          </div>
                                          {isTaskExpanded && (
                                            <div className="mt-2 pt-2 border-t border-slate-600 text-xs space-y-1">
                                              {task.details && <p className="text-muted-foreground">{task.details}</p>}
                                              <div className="flex items-center gap-3 text-muted-foreground">
                                                {task.assigned_by && <span>Assigned by: {task.assigned_by}</span>}
                                                {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                                                {task.completed_by && <span className="text-green-400">✓ {task.completed_by}</span>}
                                              </div>
                                              {(isAdmin || isLeadership) && (
                                                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400" onClick={() => removeTask(phase.id, i, task.id)}>
                                                  <Trash2 className="h-3 w-3 mr-1" /> Remove
                                                </Button>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>

                                  {/* Add Task Form */}
                                  {showTaskForm?.phaseId === phase.id && showTaskForm?.itemIdx === i && (
                                    <div className="mt-2 p-2 bg-slate-700/50 rounded border border-slate-600 space-y-2">
                                      <Input value={taskForm.name} onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Task name..." className="h-8 text-xs" />
                                      <Textarea value={taskForm.details} onChange={(e) => setTaskForm(prev => ({ ...prev, details: e.target.value }))} placeholder="Details (optional)..." rows={2} className="text-xs" />
                                      <div className="grid grid-cols-2 gap-2">
                                        <Select value={taskForm.assignee} onValueChange={(v) => setTaskForm(prev => ({ ...prev, assignee: v }))}>
                                          <SelectTrigger className="h-8 text-xs">
                                            <SelectValue placeholder="Assign to..." />
                                          </SelectTrigger>
                                          <SelectContent>
                                            {allTeamMembers.map(member => (
                                              <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))} className="h-8 text-xs" />
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowTaskForm(null)}>Cancel</Button>
                                        <Button size="sm" className="h-7 text-xs" onClick={() => addTask(phase.id, i, taskForm)} disabled={!taskForm.name?.trim()}>
                                          <Plus className="h-3 w-3 mr-1" /> Create
                                        </Button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Add Item Input */}
                      {isAdmin && (
                        <div className="flex gap-2">
                          <Input 
                            value={newItemInputs[phase.id] || ''} 
                            onChange={(e) => setNewItemInputs(prev => ({ ...prev, [phase.id]: e.target.value }))}
                            placeholder={`Add ${phase.name.toLowerCase()} item...`}
                            className="h-8 text-xs"
                            onKeyDown={(e) => e.key === 'Enter' && addItem(phase.id)} 
                          />
                          <Button size="sm" className="h-8" onClick={() => addItem(phase.id)}><Plus className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Notes Section */}
          <div>
            <Label>Notes</Label>
            <Textarea 
              value={content.notes || ''} 
              onChange={(e) => setContent({...content, notes: e.target.value})}
              disabled={!isAdmin && !isLeadership}
              className="mt-1" 
              rows={3}
              placeholder="Add security notes, audit findings, or discussion points..."
            />
          </div>
          
          {/* Feedback Section for Leadership */}
          <GovernanceFeedbackSection 
            pillar={pillar} 
            isAdmin={isAdmin} 
            isLeadership={isLeadership} 
            currentUser={currentUser} 
            onSave={onSave} 
          />
        </div>
        {(isAdmin || isLeadership) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

const CostDialog = ({ pillar, open, onOpenChange, onSave, isAdmin, isLeadership, currentUser, onRefresh }) => {
  const [content, setContent] = useState(pillar?.content || {});
  const [saving, setSaving] = useState(false);
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});
  const [showTaskForm, setShowTaskForm] = useState(null);
  const [taskForm, setTaskForm] = useState({ name: '', details: '', assignee: '', due_date: '' });
  const [platformUsers, setPlatformUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showExpenseForm, setShowExpenseForm] = useState(null);
  const [expenseForm, setExpenseForm] = useState({ name: '', date: '', amount: '', reason: '', approved_by: '' });
  const [showVendorList, setShowVendorList] = useState(false);

  useEffect(() => {
    if (pillar) setContent(pillar.content || {});
  }, [pillar]);

  // Default approvers list
  const approvers = [
    { id: 'ryan_march', name: 'Ryan March' },
    { id: 'ryan_adams', name: 'Ryan Adams' },
    { id: 'gabe', name: 'Gabriel Gecan' },
  ];

  // Get vendor list from content or default
  const vendorList = content.expense_vendors || [];

  // Add vendor to saved list
  const addVendor = (vendorName) => {
    if (!vendorName?.trim() || vendorList.includes(vendorName.trim())) return;
    const updatedVendors = [...vendorList, vendorName.trim()];
    setContent({ ...content, expense_vendors: updatedVendors });
    toast.success(`"${vendorName.trim()}" added to vendor list`);
  };

  // Default team members
  const defaultTeamMembers = [
    { id: 'ryan_march', name: 'Ryan March', role: 'Lead Developer', avatar: 'RM' },
    { id: 'ryan_adams', name: 'Ryan Adams', role: 'Developer', avatar: 'RA' },
    { id: 'gabe', name: 'Gabriel Gecan', role: 'Developer', avatar: 'GG' },
  ];

  // Fetch platform users
  const fetchPlatformUsers = async () => {
    if (platformUsers.length > 0) return;
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const users = response.data.map(u => ({
        id: u.id,
        name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email,
        role: u.role?.replace(/_/g, ' '),
        avatar: `${(u.first_name || '')[0] || ''}${(u.last_name || '')[0] || ''}`.toUpperCase()
      }));
      setPlatformUsers(users);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    } finally {
      setLoadingUsers(false);
    }
  };

  const allTeamMembers = [...defaultTeamMembers, ...platformUsers.filter(pu => 
    !defaultTeamMembers.find(dm => dm.name.toLowerCase() === pu.name.toLowerCase())
  )];

  // Normalize breakdown to include tasks and expenses
  const normalizeBreakdown = (breakdown) => {
    if (!breakdown) return [];
    return breakdown.map((item, i) => ({
      ...item,
      id: item.id || Date.now() + i,
      tasks: item.tasks || [],
      expenses: item.expenses || []
    }));
  };

  const breakdown = normalizeBreakdown(content.breakdown);

  const handleSave = async () => {
    setSaving(true);
    try {
      const remaining = (content.total_budget || 0) - (content.spent_to_date || 0);
      await onSave(pillar.id, { content: { ...content, breakdown, remaining } });
      toast.success('Cost updated');
      onOpenChange(false);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const updateBreakdown = (idx, field, value) => {
    const updated = [...breakdown];
    if (field === 'budgeted' || field === 'spent') {
      updated[idx] = { ...updated[idx], [field]: parseFloat(value) || 0 };
    } else {
      updated[idx] = { ...updated[idx], [field]: value };
    }
    setContent({ ...content, breakdown: updated });
  };

  const addBreakdownItem = () => {
    if (!newCategoryInput.trim()) return;
    const newItem = { 
      id: Date.now(),
      category: newCategoryInput.trim(), 
      budgeted: 0, 
      spent: 0,
      tasks: []
    };
    setContent({ ...content, breakdown: [...breakdown, newItem] });
    setNewCategoryInput('');
  };

  const removeBreakdownItem = (idx) => {
    setContent({ ...content, breakdown: breakdown.filter((_, i) => i !== idx) });
  };

  // Task management for budget categories
  const addTask = (categoryIdx, taskData) => {
    if (!taskData.name?.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const newTask = { 
      id: Date.now(), 
      name: taskData.name.trim(), 
      details: taskData.details || '',
      assignee: taskData.assignee || '',
      due_date: taskData.due_date || '',
      completed: false,
      assigned_by: userName,
      assigned_at: new Date().toISOString(),
    };
    const updated = [...breakdown];
    updated[categoryIdx] = { 
      ...updated[categoryIdx], 
      tasks: [...(updated[categoryIdx].tasks || []), newTask] 
    };
    setContent({ ...content, breakdown: updated });
    setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
    setShowTaskForm(null);
  };

  const toggleTask = (categoryIdx, taskId) => {
    const updated = [...breakdown];
    const completedBy = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    updated[categoryIdx] = {
      ...updated[categoryIdx],
      tasks: updated[categoryIdx].tasks.map(t => 
        t.id === taskId ? { 
          ...t, 
          completed: !t.completed,
          completed_by: !t.completed ? completedBy : null,
          completed_at: !t.completed ? new Date().toISOString() : null
        } : t
      )
    };
    setContent({ ...content, breakdown: updated });
  };

  const removeTask = (categoryIdx, taskId) => {
    const updated = [...breakdown];
    updated[categoryIdx] = {
      ...updated[categoryIdx],
      tasks: updated[categoryIdx].tasks.filter(t => t.id !== taskId)
    };
    setContent({ ...content, breakdown: updated });
  };

  // Expense management
  const addExpense = (categoryIdx, expenseData) => {
    if (!expenseData.name?.trim() || !expenseData.amount) return;
    const newExpense = {
      id: Date.now(),
      name: expenseData.name.trim(),
      date: expenseData.date || new Date().toISOString().split('T')[0],
      amount: parseFloat(expenseData.amount) || 0,
      reason: expenseData.reason || '',
      approved_by: expenseData.approved_by || '',
      created_at: new Date().toISOString(),
      created_by: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin',
    };
    const updated = [...breakdown];
    const expenses = [...(updated[categoryIdx].expenses || []), newExpense];
    updated[categoryIdx] = { ...updated[categoryIdx], expenses };
    
    // Auto-update spent amount based on total expenses
    const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    updated[categoryIdx].spent = totalExpenses;
    
    // Auto-update total spent_to_date
    const totalSpent = updated.reduce((sum, cat) => sum + (cat.spent || 0), 0);
    
    setContent({ ...content, breakdown: updated, spent_to_date: totalSpent });
    setExpenseForm({ name: '', date: '', amount: '', reason: '', approved_by: '' });
    setShowExpenseForm(null);
    toast.success('Expense added');
  };

  const removeExpense = (categoryIdx, expenseId) => {
    const updated = [...breakdown];
    const expenses = updated[categoryIdx].expenses.filter(e => e.id !== expenseId);
    updated[categoryIdx] = { ...updated[categoryIdx], expenses };
    
    // Auto-update spent amount
    const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
    updated[categoryIdx].spent = totalExpenses;
    
    // Auto-update total spent_to_date
    const totalSpent = updated.reduce((sum, cat) => sum + (cat.spent || 0), 0);
    
    setContent({ ...content, breakdown: updated, spent_to_date: totalSpent });
    toast.success('Expense removed');
  };

  const getCategoryExpenseTotal = (categoryIdx) => {
    return (breakdown[categoryIdx]?.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);
  };

  const spentPercent = content.total_budget > 0 ? Math.round((content.spent_to_date / content.total_budget) * 100) : 0;
  const remaining = (content.total_budget || 0) - (content.spent_to_date || 0);

  const toggleCategoryCollapse = (idx) => {
    setCollapsedCategories(prev => ({ ...prev, [idx]: prev[idx] === false ? true : false }));
  };

  const getCategoryColor = (item) => {
    const percent = item.budgeted > 0 ? (item.spent / item.budgeted) * 100 : 0;
    if (percent > 100) return { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400' };
    if (percent > 80) return { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400' };
    return { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-500" /> Cost
          </DialogTitle>
          <DialogDescription className="flex items-center justify-between">
            <span>Budget tracking and breakdown</span>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${spentPercent > 100 ? 'text-red-500' : 'text-amber-500'}`}>
                {spentPercent}% utilized
              </span>
            </div>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Budget Summary */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-muted/50 rounded-lg text-center border">
              <p className="text-xs text-muted-foreground mb-1">Total Budget</p>
              {isAdmin ? (
                <div className="flex items-center justify-center">
                  <span className="text-sm mr-1">$</span>
                  <Input 
                    type="number" 
                    value={content.total_budget || ''} 
                    onChange={(e) => setContent({...content, total_budget: parseFloat(e.target.value) || 0})}
                    className="h-8 text-center font-bold w-28"
                  />
                </div>
              ) : (
                <p className="text-lg font-bold">${(content.total_budget || 0).toLocaleString()}</p>
              )}
            </div>
            <div className="p-3 bg-amber-500/10 rounded-lg text-center border border-amber-500/30">
              <p className="text-xs text-muted-foreground mb-1">Spent to Date</p>
              {isAdmin ? (
                <div className="flex items-center justify-center">
                  <span className="text-sm mr-1">$</span>
                  <Input 
                    type="number" 
                    value={content.spent_to_date || ''} 
                    onChange={(e) => setContent({...content, spent_to_date: parseFloat(e.target.value) || 0})}
                    className="h-8 text-center font-bold w-28 text-amber-600"
                  />
                </div>
              ) : (
                <p className="text-lg font-bold text-amber-600">${(content.spent_to_date || 0).toLocaleString()}</p>
              )}
            </div>
            <div className={`p-3 rounded-lg text-center border ${remaining >= 0 ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <p className="text-xs text-muted-foreground mb-1">Remaining</p>
              <p className={`text-lg font-bold ${remaining >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${remaining.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Budget Utilization Bar */}
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Budget Utilization</span>
              <span className={spentPercent > 100 ? 'text-red-600 font-bold' : ''}>{spentPercent}%</span>
            </div>
            <Progress value={Math.min(spentPercent, 100)} className={`h-2 ${spentPercent > 100 ? '[&>div]:bg-red-500' : ''}`} />
          </div>

          {/* Burn Rate */}
          <div>
            <Label>Burn Rate</Label>
            <Input value={content.burn_rate || ''} 
              onChange={(e) => setContent({...content, burn_rate: e.target.value})} disabled={!isAdmin} className="mt-1" 
              placeholder="e.g., $5,000/week" />
          </div>

          {/* Budget Categories with Tasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">Budget Categories</Label>
            </div>
            
            <div className="space-y-3">
              {breakdown.map((item, i) => {
                const categoryColors = getCategoryColor(item);
                const isCollapsed = collapsedCategories[i] !== false;
                const taskCount = item.tasks?.length || 0;
                const completedTasks = item.tasks?.filter(t => t.completed).length || 0;
                const categoryPercent = item.budgeted > 0 ? Math.round((item.spent / item.budgeted) * 100) : 0;

                return (
                  <div key={item.id || i} className={`rounded-lg border-2 ${categoryColors.border} ${categoryColors.bg}`}>
                    {/* Category Header */}
                    <div 
                      className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                      onClick={() => toggleCategoryCollapse(i)}
                    >
                      <div className="flex items-center gap-2">
                        {isCollapsed ? <ChevronRight className={`h-4 w-4 ${categoryColors.text}`} /> : <ChevronDown className={`h-4 w-4 ${categoryColors.text}`} />}
                        <DollarSign className={`h-4 w-4 ${categoryColors.text}`} />
                        <span className="font-semibold">{item.category}</span>
                        <Badge variant="outline" className={`text-[10px] ${categoryColors.border}`}>
                          ${item.spent?.toLocaleString()} / ${item.budgeted?.toLocaleString()}
                        </Badge>
                        {(item.expenses?.length || 0) > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            {item.expenses.length} expenses
                          </Badge>
                        )}
                        {taskCount > 0 && (
                          <Badge variant="secondary" className="text-[10px]">
                            {completedTasks}/{taskCount} tasks
                          </Badge>
                        )}
                        {categoryPercent > 100 && (
                          <Badge variant="destructive" className="text-[9px]">Over Budget</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs ${categoryColors.text}`}>{categoryPercent}%</span>
                        <Progress value={Math.min(categoryPercent, 100)} className="w-20 h-1.5" />
                      </div>
                    </div>

                    {/* Category Content */}
                    {!isCollapsed && (
                      <div className="px-3 pb-3 space-y-3 border-t border-slate-700/50">
                        {/* Budget Fields */}
                        {isAdmin && (
                          <div className="grid grid-cols-4 gap-2 pt-2">
                            <div className="col-span-2">
                              <Label className="text-[10px] text-muted-foreground">Category Name</Label>
                              <Input 
                                value={item.category} 
                                onChange={(e) => updateBreakdown(i, 'category', e.target.value)}
                                className="h-8 text-xs"
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-muted-foreground">Budgeted ($)</Label>
                              <Input 
                                type="number" 
                                value={item.budgeted || ''} 
                                onChange={(e) => updateBreakdown(i, 'budgeted', e.target.value)} 
                                className="h-8 text-xs" 
                              />
                            </div>
                            <div className="flex items-end gap-1">
                              <div className="flex-1">
                                <Label className="text-[10px] text-muted-foreground">Spent ($)</Label>
                                <Input 
                                  type="number" 
                                  value={item.spent || ''} 
                                  onChange={(e) => updateBreakdown(i, 'spent', e.target.value)} 
                                  className="h-8 text-xs" 
                                />
                              </div>
                              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeBreakdownItem(i)}>
                                <Trash2 className="h-3 w-3 text-red-400" />
                              </Button>
                            </div>
                          </div>
                        )}

                        {/* Progress */}
                        <div className="flex items-center gap-2">
                          <Progress value={Math.min(categoryPercent, 100)} className={`flex-1 h-1.5 ${categoryPercent > 100 ? '[&>div]:bg-red-500' : ''}`} />
                          <span className="text-xs text-muted-foreground">{categoryPercent}%</span>
                        </div>

                        {/* Tasks Section */}
                        <div className="pt-2 border-t border-slate-700">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs flex items-center gap-2">
                              Related Tasks
                              <Badge variant="secondary" className="text-[10px]">{completedTasks}/{taskCount}</Badge>
                            </Label>
                            {(isAdmin || isLeadership) && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 text-[10px] px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowTaskForm(showTaskForm?.categoryIdx === i ? null : { categoryIdx: i });
                                  setTaskForm({ name: '', details: '', assignee: '', due_date: '' });
                                  fetchPlatformUsers();
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add Task
                              </Button>
                            )}
                          </div>

                          {/* Task List */}
                          <div className="space-y-1">
                            {item.tasks?.map((task) => {
                              const isTaskExpanded = expandedTasks[task.id];
                              return (
                                <div key={task.id} className={`p-2 rounded border ${task.completed ? 'bg-green-500/10 border-green-500/30' : 'bg-slate-700/50 border-slate-600'}`}>
                                  <div 
                                    className="flex items-center gap-2 cursor-pointer"
                                    onClick={() => setExpandedTasks(prev => ({ ...prev, [task.id]: !prev[task.id] }))}
                                  >
                                    <Checkbox 
                                      checked={task.completed}
                                      onCheckedChange={() => toggleTask(i, task.id)}
                                      onClick={(e) => e.stopPropagation()}
                                      disabled={!isAdmin && !isLeadership}
                                    />
                                    <span className={`flex-1 text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.name}</span>
                                    {task.assignee && (
                                      <Badge variant="outline" className="text-[10px]">
                                        {allTeamMembers.find(m => m.id === task.assignee)?.name || task.assignee}
                                      </Badge>
                                    )}
                                    {isTaskExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                                  </div>
                                  {isTaskExpanded && (
                                    <div className="mt-2 pt-2 border-t border-slate-600 text-xs space-y-1">
                                      {task.details && <p className="text-muted-foreground">{task.details}</p>}
                                      <div className="flex items-center gap-3 text-muted-foreground">
                                        {task.assigned_by && <span>Assigned by: {task.assigned_by}</span>}
                                        {task.due_date && <span>Due: {new Date(task.due_date).toLocaleDateString()}</span>}
                                        {task.completed_by && <span className="text-green-400">✓ {task.completed_by}</span>}
                                      </div>
                                      {(isAdmin || isLeadership) && (
                                        <Button variant="ghost" size="sm" className="h-6 text-[10px] text-red-400" onClick={() => removeTask(i, task.id)}>
                                          <Trash2 className="h-3 w-3 mr-1" /> Remove
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Add Task Form */}
                          {showTaskForm?.categoryIdx === i && (
                            <div className="mt-2 p-2 bg-slate-700/50 rounded border border-slate-600 space-y-2">
                              <Input value={taskForm.name} onChange={(e) => setTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Task name..." className="h-8 text-xs" />
                              <Textarea value={taskForm.details} onChange={(e) => setTaskForm(prev => ({ ...prev, details: e.target.value }))} placeholder="Details (optional)..." rows={2} className="text-xs" />
                              <div className="grid grid-cols-2 gap-2">
                                <Select value={taskForm.assignee} onValueChange={(v) => setTaskForm(prev => ({ ...prev, assignee: v }))}>
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Assign to..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {allTeamMembers.map(member => (
                                      <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))} className="h-8 text-xs" />
                              </div>
                              <div className="flex justify-end gap-2">
                                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowTaskForm(null)}>Cancel</Button>
                                <Button size="sm" className="h-7 text-xs" onClick={() => addTask(i, taskForm)} disabled={!taskForm.name?.trim()}>
                                  <Plus className="h-3 w-3 mr-1" /> Create
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Expenses Section */}
                        <div className="pt-2 border-t border-slate-700">
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs flex items-center gap-2">
                              <DollarSign className="h-3 w-3" />
                              Expenses
                              <Badge variant="secondary" className="text-[10px]">
                                ${getCategoryExpenseTotal(i).toLocaleString()}
                              </Badge>
                              <Badge variant="outline" className="text-[10px]">
                                {item.expenses?.length || 0} items
                              </Badge>
                            </Label>
                            {isAdmin && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="h-6 text-[10px] px-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowExpenseForm(showExpenseForm?.categoryIdx === i ? null : { categoryIdx: i });
                                  setExpenseForm({ name: '', date: new Date().toISOString().split('T')[0], amount: '', reason: '', approved_by: '' });
                                }}
                              >
                                <Plus className="h-3 w-3 mr-1" /> Add Expense
                              </Button>
                            )}
                          </div>

                          {/* Expense List Table */}
                          {item.expenses?.length > 0 && (
                            <div className="rounded border border-slate-600 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead className="bg-slate-700/50">
                                  <tr>
                                    <th className="px-2 py-1.5 text-left font-medium">Vendor/Name</th>
                                    <th className="px-2 py-1.5 text-left font-medium">Date</th>
                                    <th className="px-2 py-1.5 text-right font-medium">Amount</th>
                                    <th className="px-2 py-1.5 text-left font-medium">Approved By</th>
                                    <th className="px-2 py-1.5 text-left font-medium">Reason</th>
                                    {isAdmin && <th className="px-2 py-1.5 w-8"></th>}
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.expenses.map((expense) => (
                                    <tr key={expense.id} className="border-t border-slate-600/50 hover:bg-slate-700/30">
                                      <td className="px-2 py-1.5 font-medium">{expense.name}</td>
                                      <td className="px-2 py-1.5 text-muted-foreground">
                                        {expense.date ? new Date(expense.date).toLocaleDateString() : '-'}
                                      </td>
                                      <td className="px-2 py-1.5 text-right text-amber-400 font-medium">
                                        ${expense.amount?.toLocaleString()}
                                      </td>
                                      <td className="px-2 py-1.5">
                                        <Badge variant="outline" className="text-[9px]">
                                          {approvers.find(a => a.id === expense.approved_by)?.name || expense.approved_by || '-'}
                                        </Badge>
                                      </td>
                                      <td className="px-2 py-1.5 text-muted-foreground max-w-[150px] truncate" title={expense.reason}>
                                        {expense.reason || '-'}
                                      </td>
                                      {isAdmin && (
                                        <td className="px-2 py-1.5">
                                          <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-5 w-5"
                                            onClick={() => removeExpense(i, expense.id)}
                                          >
                                            <Trash2 className="h-3 w-3 text-red-400" />
                                          </Button>
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {/* Add Expense Form */}
                          {showExpenseForm?.categoryIdx === i && (
                            <div className="mt-2 p-3 bg-slate-700/50 rounded border border-slate-600 space-y-3" onClick={(e) => e.stopPropagation()}>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Vendor/Expense Name *</Label>
                                  <div className="flex gap-1 mt-1">
                                    <Input 
                                      value={expenseForm.name} 
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        setExpenseForm(prev => ({ ...prev, name: e.target.value }));
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="Enter vendor name..." 
                                      className="h-8 text-xs flex-1"
                                      list={`vendor-list-${i}`}
                                      autoComplete="off"
                                    />
                                    <datalist id={`vendor-list-${i}`}>
                                      {vendorList.map((v, idx) => (
                                        <option key={idx} value={v} />
                                      ))}
                                    </datalist>
                                    <Button 
                                      size="sm" 
                                      variant="outline" 
                                      className={`h-8 text-[10px] px-2 ${expenseForm.name && !vendorList.includes(expenseForm.name) ? '' : 'invisible'}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        addVendor(expenseForm.name);
                                      }}
                                      title="Save to vendor list"
                                    >
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Date</Label>
                                  <Input 
                                    type="date" 
                                    value={expenseForm.date} 
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setExpenseForm(prev => ({ ...prev, date: e.target.value }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="h-8 text-xs mt-1" 
                                  />
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Amount ($) *</Label>
                                  <Input 
                                    type="number" 
                                    value={expenseForm.amount} 
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setExpenseForm(prev => ({ ...prev, amount: e.target.value }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="0.00" 
                                    className="h-8 text-xs mt-1" 
                                  />
                                </div>
                                <div>
                                  <Label className="text-[10px] text-muted-foreground">Approved By</Label>
                                  <Select value={expenseForm.approved_by} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, approved_by: v }))}>
                                    <SelectTrigger className="h-8 text-xs mt-1" onClick={(e) => e.stopPropagation()}>
                                      <SelectValue placeholder="Select approver..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {approvers.map(approver => (
                                        <SelectItem key={approver.id} value={approver.id}>{approver.name}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <div>
                                <Label className="text-[10px] text-muted-foreground">Reason / Details</Label>
                                <Textarea 
                                  value={expenseForm.reason} 
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setExpenseForm(prev => ({ ...prev, reason: e.target.value }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder="Detailed reason for this expense..." 
                                  rows={2} 
                                  className="text-xs mt-1" 
                                />
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-[10px] text-muted-foreground">
                                  {vendorList.length > 0 && `${vendorList.length} saved vendors`}
                                </span>
                                <div className="flex gap-2">
                                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); setShowExpenseForm(null); }}>Cancel</Button>
                                  <Button 
                                    size="sm" 
                                    className="h-7 text-xs" 
                                    onClick={(e) => { e.stopPropagation(); addExpense(i, expenseForm); }} 
                                    disabled={!expenseForm.name?.trim() || !expenseForm.amount}
                                  >
                                    <Plus className="h-3 w-3 mr-1" /> Add Expense
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Add Category */}
              {isAdmin && (
                <div className="flex gap-2">
                  <Input 
                    value={newCategoryInput} 
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="Add budget category..."
                    className="h-8 text-xs"
                    onKeyDown={(e) => e.key === 'Enter' && addBreakdownItem()} 
                  />
                  <Button size="sm" className="h-8" onClick={addBreakdownItem}><Plus className="h-4 w-4" /></Button>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label>Notes</Label>
            <Textarea value={content.notes || ''} 
              onChange={(e) => setContent({...content, notes: e.target.value})} disabled={!isAdmin} className="mt-1" rows={2} />
          </div>
          
          {/* Feedback Section for Leadership */}
          <GovernanceFeedbackSection 
            pillar={pillar} 
            isAdmin={isAdmin} 
            isLeadership={isLeadership} 
            currentUser={currentUser} 
            onSave={onSave} 
          />
        </div>
        {(isAdmin || isLeadership) && (
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
// ==================== MAIN WIDGET ====================
export const FivePillarsWidget = ({ isAdmin = false, compact = false, embedded = false, showOnly = null }) => {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGovPillar, setSelectedGovPillar] = useState(null);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [featureSheetOpen, setFeatureSheetOpen] = useState(false);
  const [addFeatureOpen, setAddFeatureOpen] = useState(false);
  const [featuresCollapsed, setFeaturesCollapsed] = useState(false); // State for collapsing features section
  
  // Enhanced Scope Modal state (consistent with main 5 Pillars Hub)
  const [showEnhancedScopeModal, setShowEnhancedScopeModal] = useState(false);
  const [scopeData, setScopeData] = useState({ mvp_items: [], future_items: [], metrics: {} });
  const [scopeLoading, setScopeLoading] = useState(false);
  const [selectedScopeItem, setSelectedScopeItem] = useState(null);
  const [pendingScopeProgress, setPendingScopeProgress] = useState(null);

  // Determine if current user is leadership (can send suggestions)
  const isLeadership = LEADERSHIP_ROLES.includes(user?.role);

  // Determine what to show based on showOnly prop
  const showPillars = !showOnly || showOnly === 'pillars';
  const showFeatures = !showOnly || showOnly === 'features';

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/governance/five-pillars`);
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config:', error);
      if (!embedded) toast.error('Failed to load Five Pillars');
    } finally {
      setLoading(false);
    }
  }, [embedded]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const handleGovPillarClick = (pillar) => setSelectedGovPillar(pillar);

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setFeatureSheetOpen(true);
  };

  const handleSaveGovPillar = async (pillarId, data) => {
    await axios.put(`${API}/api/governance/five-pillars/governance/${pillarId}`, data);
    const response = await axios.get(`${API}/api/governance/five-pillars`);
    setConfig(response.data);
  };

  const handleSaveFeature = async (feature) => {
    await axios.put(`${API}/api/governance/five-pillars/features/${feature.id}`, feature);
    const response = await axios.get(`${API}/api/governance/five-pillars`);
    setConfig(response.data);
    const fresh = response.data.feature_pillars?.find(p => p.id === feature.id);
    if (fresh) setSelectedFeature(fresh);
  };

  const handleAddFeature = async (data) => {
    await axios.post(`${API}/api/governance/five-pillars/features`, data);
    fetchConfig();
    toast.success('Feature added');
  };

  // ==================== ENHANCED SCOPE MODAL HANDLERS ====================
  const fetchScopeData = useCallback(async () => {
    setScopeLoading(true);
    try {
      const response = await axios.get(`${API}/api/governance/five-pillars/scope`);
      setScopeData(response.data || { mvp_items: [], future_items: [], metrics: {} });
    } catch (error) {
      console.error('Failed to fetch scope data:', error);
      toast.error('Failed to load scope items');
    } finally {
      setScopeLoading(false);
    }
  }, []);

  const handleOpenEnhancedScope = () => {
    setSelectedGovPillar(null); // Close legacy dialog
    setShowEnhancedScopeModal(true);
    fetchScopeData();
  };

  const updateScopeItem = async (itemId, updates) => {
    try {
      await axios.patch(`${API}/api/governance/five-pillars/scope/${itemId}`, updates);
      // Refresh scope data
      const response = await axios.get(`${API}/api/governance/five-pillars/scope`);
      setScopeData(response.data || { mvp_items: [], future_items: [], metrics: {} });
      toast.success('Scope item updated');
    } catch (error) {
      console.error('Failed to update scope item:', error);
      toast.error('Failed to update scope item');
    }
  };

  const deleteScopeItem = async (itemId) => {
    try {
      await axios.delete(`${API}/api/governance/five-pillars/scope/${itemId}`);
      const response = await axios.get(`${API}/api/governance/five-pillars/scope`);
      setScopeData(response.data || { mvp_items: [], future_items: [], metrics: {} });
      toast.success('Scope item deleted');
    } catch (error) {
      console.error('Failed to delete scope item:', error);
      toast.error('Failed to delete scope item');
    }
  };

  // Calculate total unread notification count across all governance pillars
  const getTotalUnreadCount = () => {
    if (!config?.governance_pillars) return 0;
    let total = 0;
    
    config.governance_pillars.forEach(pillar => {
      if (isAdmin) {
        // Admin sees: unresolved suggestions + unseen leadership task updates
        total += pillar.suggestions?.filter(s => !s.resolved).length || 0;
        
        // Count leadership task updates
        const countLeadershipUpdates = (items) => {
          if (!Array.isArray(items)) return 0;
          let count = 0;
          items.forEach(item => {
            (item.tasks || []).forEach(task => {
              const createdByLeadership = task.assigned_by && !['super_admin', 'admin'].includes(task.created_by_role);
              const hasUnseenUpdates = task.updates?.some(u => !u.seen_by?.includes(user?.id) && !u.is_admin);
              const taskNotSeen = !task.seen_by?.includes(user?.id) && createdByLeadership;
              if (taskNotSeen || hasUnseenUpdates) count++;
            });
          });
          return count;
        };
        
        total += countLeadershipUpdates(pillar.content?.key_deliverables || []);
        total += countLeadershipUpdates(pillar.content?.key_milestones || []);
        if (pillar.content?.phases) {
          Object.values(pillar.content.phases).forEach(phaseItems => {
            total += countLeadershipUpdates(phaseItems);
          });
        }
      } else if (isLeadership) {
        // Leadership sees: unseen admin replies + unseen admin task updates
        total += pillar.suggestions?.filter(s => {
          if (!s.replies || s.replies.length === 0) return false;
          const lastReply = s.replies[s.replies.length - 1];
          const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
          const hasSeen = s.seen_by && s.seen_by.includes(user?.id);
          return hasAdminReply && !hasSeen;
        }).length || 0;
        
        // Count admin task updates
        const countAdminUpdates = (items) => {
          if (!Array.isArray(items)) return 0;
          let count = 0;
          items.forEach(item => {
            (item.tasks || []).forEach(task => {
              const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(user?.id));
              const completedByAdmin = task.completed && task.completed_by && !task.completion_seen_by?.includes(user?.id);
              if (hasUnseenAdminUpdates || completedByAdmin) count++;
            });
          });
          return count;
        };
        
        total += countAdminUpdates(pillar.content?.key_deliverables || []);
        total += countAdminUpdates(pillar.content?.key_milestones || []);
        if (pillar.content?.phases) {
          Object.values(pillar.content.phases).forEach(phaseItems => {
            total += countAdminUpdates(phaseItems);
          });
        }
      }
    });
    
    return total;
  };

  // Mark all notifications as read across all governance pillars
  const [markingAllRead, setMarkingAllRead] = useState(false);
  
  const handleMarkAllAsRead = async () => {
    if (!config?.governance_pillars || !user?.id) return;
    
    setMarkingAllRead(true);
    const userId = user.id;
    const token = localStorage.getItem('access_token');
    
    try {
      const updatePromises = config.governance_pillars.map(async (pillar) => {
        let needsUpdate = false;
        let updatedContent = { ...pillar.content };
        let updatedSuggestions = [...(pillar.suggestions || [])];
        
        // Helper to mark items as seen
        const markItemsAsSeen = (items) => {
          if (!Array.isArray(items)) return items;
          return items.map(item => {
            const updatedTasks = (item.tasks || []).map(task => {
              let updatedTask = { ...task };
              
              if (isAdmin) {
                // Admin marks leadership updates as seen
                const hasUnseenLeadershipUpdates = task.updates?.some(u => !u.is_admin && !u.seen_by?.includes(userId));
                if (hasUnseenLeadershipUpdates) {
                  needsUpdate = true;
                  updatedTask.updates = task.updates.map(u => {
                    if (!u.is_admin && !u.seen_by?.includes(userId)) {
                      return { ...u, seen_by: [...(u.seen_by || []), userId] };
                    }
                    return u;
                  });
                }
                // Mark task as seen if created by leadership
                const createdByLeadership = task.assigned_by && !['super_admin', 'admin'].includes(task.created_by_role);
                if (createdByLeadership && !task.seen_by?.includes(userId)) {
                  needsUpdate = true;
                  updatedTask.seen_by = [...(task.seen_by || []), userId];
                }
              } else if (isLeadership) {
                // Leadership marks admin updates as seen
                const hasUnseenAdminUpdates = task.updates?.some(u => u.is_admin && !u.seen_by?.includes(userId));
                if (hasUnseenAdminUpdates) {
                  needsUpdate = true;
                  updatedTask.updates = task.updates.map(u => {
                    if (u.is_admin && !u.seen_by?.includes(userId)) {
                      return { ...u, seen_by: [...(u.seen_by || []), userId] };
                    }
                    return u;
                  });
                }
                // Mark completion as seen
                if (task.completed && task.completed_by && !task.completion_seen_by?.includes(userId)) {
                  needsUpdate = true;
                  updatedTask.completion_seen_by = [...(task.completion_seen_by || []), userId];
                }
              }
              
              return updatedTask;
            });
            return { ...item, tasks: updatedTasks };
          });
        };
        
        // Process legacy structures
        if (pillar.content?.key_deliverables) {
          updatedContent.key_deliverables = markItemsAsSeen(pillar.content.key_deliverables);
        }
        if (pillar.content?.key_milestones) {
          updatedContent.key_milestones = markItemsAsSeen(pillar.content.key_milestones);
        }
        
        // Process phases object
        if (pillar.content?.phases && typeof pillar.content.phases === 'object') {
          const updatedPhases = {};
          Object.entries(pillar.content.phases).forEach(([phaseId, phaseItems]) => {
            updatedPhases[phaseId] = markItemsAsSeen(phaseItems);
          });
          updatedContent.phases = updatedPhases;
        }
        
        // Mark suggestions as seen (for leadership)
        if (isLeadership && pillar.suggestions) {
          updatedSuggestions = pillar.suggestions.map(s => {
            if (!s.replies || s.replies.length === 0) return s;
            const lastReply = s.replies[s.replies.length - 1];
            const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
            const hasSeen = s.seen_by && s.seen_by.includes(userId);
            if (hasAdminReply && !hasSeen) {
              needsUpdate = true;
              return { ...s, seen_by: [...(s.seen_by || []), userId] };
            }
            return s;
          });
        }
        
        if (needsUpdate) {
          await axios.put(`${API}/api/governance/five-pillars/governance/${pillar.id}`, 
            { content: updatedContent, suggestions: updatedSuggestions },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
      });
      
      await Promise.all(updatePromises);
      await fetchConfig();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all as read:', error);
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const totalUnreadCount = getTotalUnreadCount();

  if (loading) {
    return embedded ? <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div> :
      <Card><CardContent className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></CardContent></Card>;
  }

  if (!config?.enabled) return embedded ? <div className="text-center py-8 text-muted-foreground text-sm">Five Pillars not enabled</div> : null;

  const govPillars = config.governance_pillars || [];
  const featurePillars = config.feature_pillars || [];
  const overallProgress = featurePillars.length > 0 ? Math.round(featurePillars.reduce((a, p) => a + (p.progress_percent || 0), 0) / featurePillars.length) : 0;
  const liveFeatures = featurePillars.filter(p => p.status === 'live').length;
  
  // Find the scope pillar (source of truth for phase statuses)
  const scopePillar = govPillars.find(p => p.pillar_type === 'scope');

  // Get dialog for selected governance pillar
  const getGovDialog = () => {
    if (!selectedGovPillar) return null;
    
    const props = { 
      pillar: selectedGovPillar, 
      open: !!selectedGovPillar, 
      onOpenChange: (o) => !o && setSelectedGovPillar(null), 
      onSave: handleSaveGovPillar, 
      isAdmin,
      isLeadership,
      currentUser: user,
      onRefresh: fetchConfig // Pass refresh callback to dialog
    };
    switch (selectedGovPillar.pillar_type) {
      case 'scope': 
        // Use Enhanced Scope Modal (consistent with main 5 Pillars Hub)
        // Trigger it automatically when scope pillar is selected
        if (!showEnhancedScopeModal) {
          handleOpenEnhancedScope();
        }
        return null; // EnhancedScopeModal is rendered separately
      case 'timeline': return <TimelineDialog {...props} scopePillar={scopePillar} />;
      case 'implementation': return <ImplementationDialog {...props} scopePillar={scopePillar} />;
      case 'data_security': return <DataSecurityDialog {...props} />;
      case 'cost': return <CostDialog {...props} />;
      default: return null;
    }
  };

  if (embedded) {
    return (
      <>
        <div className="space-y-6" data-testid="five-pillars-embedded">
          {/* Mark All Read Button for embedded view */}
          {(isAdmin || isLeadership) && totalUnreadCount > 0 && (
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleMarkAllAsRead}
                disabled={markingAllRead}
                className="relative"
                data-testid="mark-all-read-btn-embedded"
              >
                {markingAllRead ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCheck className="h-4 w-4 mr-2" />
                )}
                Mark All Read
                <Badge className="ml-2 h-5 min-w-5 px-1.5 bg-blue-500 hover:bg-blue-500">{totalUnreadCount}</Badge>
              </Button>
            </div>
          )}
          {/* Governance Pillars - Show if showPillars is true */}
          {showPillars && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Development Pillars</h3>
                <Badge className="bg-blue-500 text-white hover:bg-blue-500 text-[10px] px-2 py-0.5">Beta</Badge>
              </div>
              <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                {govPillars.map(p => <GovernancePillarCard key={p.id} pillar={p} onClick={handleGovPillarClick} isAdmin={isAdmin} isLeadership={isLeadership} currentUserId={user?.id} scopePillar={scopePillar} />)}
              </div>
            </div>
          )}
          {/* Features - Show if showFeatures is true */}
          {showFeatures && (
            <div>
              <div 
                className="flex items-center justify-between mb-3 cursor-pointer hover:bg-muted/30 -mx-2 px-2 py-1 rounded-lg transition-colors"
                onClick={() => setFeaturesCollapsed(!featuresCollapsed)}
              >
                <div className="flex items-center gap-2">
                  {featuresCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Features</h3>
                  <Badge className="bg-yellow-500 text-yellow-950 hover:bg-yellow-500 text-[10px] px-2 py-0.5">Under Construction</Badge>
                </div>
                <div className="flex items-center gap-4 text-sm" onClick={(e) => e.stopPropagation()}>
                  <span><strong>{overallProgress}%</strong> overall</span>
                  <span><strong>{liveFeatures}/{featurePillars.length}</strong> live</span>
                  {isAdmin && <Button size="sm" variant="outline" onClick={() => setAddFeatureOpen(true)}><Plus className="h-3 w-3 mr-1" />Add</Button>}
                </div>
              </div>
              {!featuresCollapsed && (
                <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                  {featurePillars.map(p => <FeaturePillarCard key={p.id} pillar={p} onClick={handleFeatureClick} isAdmin={isAdmin} isLeadership={isLeadership} currentUserId={user?.id} />)}
                </div>
              )}
            </div>
          )}
        </div>
        {getGovDialog()}
        <FeatureDetailSheet pillar={selectedFeature} open={featureSheetOpen} onOpenChange={setFeatureSheetOpen} isAdmin={isAdmin} isLeadership={isLeadership} currentUser={user} onSave={handleSaveFeature} />
        <AddFeatureDialog open={addFeatureOpen} onOpenChange={setAddFeatureOpen} onAdd={handleAddFeature} />
        
        {/* Enhanced Scope Modal - Consistent with main 5 Pillars Hub */}
        <EnhancedScopeModal
          open={showEnhancedScopeModal}
          onOpenChange={(open) => {
            setShowEnhancedScopeModal(open);
            if (!open) {
              setSelectedScopeItem(null);
              setPendingScopeProgress(null);
            }
          }}
          scopeData={scopeData}
          scopeLoading={scopeLoading}
          canEdit={isAdmin}
          selectedItem={selectedScopeItem}
          pendingProgress={pendingScopeProgress}
          onSelectItem={setSelectedScopeItem}
          onSetPendingProgress={setPendingScopeProgress}
          onUpdateItem={(id, updates) => {
            updateScopeItem(id, updates);
            if (selectedScopeItem?.id === id) {
              setSelectedScopeItem({ ...selectedScopeItem, ...updates });
            }
          }}
          onDeleteItem={(id) => {
            deleteScopeItem(id);
            setSelectedScopeItem(null);
          }}
          onAddMvpItem={() => {
            setShowEnhancedScopeModal(false);
            toast.info('Navigate to 5 Pillars Hub to add scope items');
          }}
          onAddFutureItem={() => {
            setShowEnhancedScopeModal(false);
            toast.info('Navigate to 5 Pillars Hub to add scope items');
          }}
        />
      </>
    );
  }

  return (
    <>
      <Card className="border-2 border-indigo-500/30" data-testid="five-pillars-widget">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-500/10"><Building2 className="h-5 w-5 text-indigo-500" /></div>
              <div>
                <CardTitle className="text-lg">{config.title || 'Five Pillars Governance'}</CardTitle>
                <CardDescription className="text-xs">{config.subtitle || 'RHINO-1 Development Overview'}</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {(isAdmin || isLeadership) && totalUnreadCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleMarkAllAsRead}
                  disabled={markingAllRead}
                  className="relative"
                  data-testid="mark-all-read-btn"
                >
                  {markingAllRead ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCheck className="h-4 w-4 mr-2" />
                  )}
                  Mark All Read
                  <Badge className="ml-2 h-5 min-w-5 px-1.5 bg-blue-500 hover:bg-blue-500">{totalUnreadCount}</Badge>
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={fetchConfig}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Governance Pillars */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Development Pillars</h3>
              <Badge className="bg-blue-500 text-white hover:bg-blue-500 text-[9px] px-1.5 py-0">Beta</Badge>
            </div>
            <div className="grid gap-2 grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
              {govPillars.map(p => <GovernancePillarCard key={p.id} pillar={p} onClick={handleGovPillarClick} isAdmin={isAdmin} isLeadership={isLeadership} currentUserId={user?.id} scopePillar={scopePillar} />)}
            </div>
          </div>
          <Separator />
          {/* Features */}
          <div>
            <div 
              className="flex items-center justify-between mb-3 cursor-pointer hover:bg-muted/30 -mx-2 px-2 py-1 rounded-lg transition-colors"
              onClick={() => setFeaturesCollapsed(!featuresCollapsed)}
            >
              <div className="flex items-center gap-2">
                {featuresCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Features</h3>
                <Badge className="bg-yellow-500 text-yellow-950 hover:bg-yellow-500 text-[9px] px-1.5 py-0">Under Construction</Badge>
              </div>
              <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-3 text-sm">
                  <span className="font-bold text-indigo-600">{overallProgress}%</span>
                  <span className="font-bold text-green-600">{liveFeatures}/{featurePillars.length}</span>
                </div>
                {isAdmin && <Button size="sm" variant="outline" onClick={() => setAddFeatureOpen(true)}><Plus className="h-3 w-3 mr-1" />Add Feature</Button>}
              </div>
            </div>
            {!featuresCollapsed && (
              <div className={`grid gap-3 ${compact ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'}`}>
                {featurePillars.map(p => <FeaturePillarCard key={p.id} pillar={p} onClick={handleFeatureClick} isAdmin={isAdmin} isLeadership={isLeadership} currentUserId={user?.id} />)}
              </div>
            )}
          </div>
          {config.updated_at && <p className="text-[10px] text-muted-foreground text-right">Last updated: {new Date(config.updated_at).toLocaleString()}</p>}
        </CardContent>
      </Card>
      {getGovDialog()}
      <FeatureDetailSheet pillar={selectedFeature} open={featureSheetOpen} onOpenChange={setFeatureSheetOpen} isAdmin={isAdmin} isLeadership={isLeadership} currentUser={user} onSave={handleSaveFeature} />
      <AddFeatureDialog open={addFeatureOpen} onOpenChange={setAddFeatureOpen} onAdd={handleAddFeature} />
      
      {/* Enhanced Scope Modal - Consistent with main 5 Pillars Hub */}
      <EnhancedScopeModal
        open={showEnhancedScopeModal}
        onOpenChange={(open) => {
          setShowEnhancedScopeModal(open);
          if (!open) {
            setSelectedScopeItem(null);
            setPendingScopeProgress(null);
          }
        }}
        scopeData={scopeData}
        scopeLoading={scopeLoading}
        canEdit={isAdmin}
        selectedItem={selectedScopeItem}
        pendingProgress={pendingScopeProgress}
        onSelectItem={setSelectedScopeItem}
        onSetPendingProgress={setPendingScopeProgress}
        onUpdateItem={(id, updates) => {
          updateScopeItem(id, updates);
          if (selectedScopeItem?.id === id) {
            setSelectedScopeItem({ ...selectedScopeItem, ...updates });
          }
        }}
        onDeleteItem={(id) => {
          deleteScopeItem(id);
          setSelectedScopeItem(null);
        }}
        onAddMvpItem={() => {
          // Close modal and redirect to main 5 Pillars Hub for adding
          setShowEnhancedScopeModal(false);
          toast.info('Navigate to 5 Pillars Hub to add scope items');
        }}
        onAddFutureItem={() => {
          setShowEnhancedScopeModal(false);
          toast.info('Navigate to 5 Pillars Hub to add scope items');
        }}
      />
    </>
  );
};

export default FivePillarsWidget;
